import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar
} from 'recharts';
import {
  Heart, Zap, Moon, Search, Bell, Sparkles, CheckCircle2,
  Mic, MicOff, Droplets, TrendingUp, AlertTriangle, Loader2,
  LayoutDashboard, MessageSquare, Activity, Thermometer, User, Settings, Camera, ShieldAlert, PhoneCall,
  FileText, Calendar, Clock, Video, Stethoscope, Phone, Wind
} from 'lucide-react';

const API_BASE_URL = 'https://backend.mednevo.com';

const WELLNESS_CHIPS = [
  {
    id: 'diet',
    label: 'Diet Plan',
    icon: '🥗',
    prompt: (v) => `Give me a personalized Indian diet plan for today. My vitals: Heart Rate ${v.hr}bpm, Steps ${v.steps}, Sleep ${v.sleep}hrs, Water intake ${v.hydration}L. Include breakfast, lunch, dinner and snacks with portions.`
  },
  {
    id: 'meal',
    label: 'Scan Meal',
    icon: '📸',
    prompt: null, // This will trigger camera
    action: 'scan_food'
  },
  {
    id: 'exercise',
    label: 'Exercise',
    icon: '💪',
    prompt: (v) => `Suggest a personalized exercise routine for today. My vitals: HR ${v.hr}bpm, Steps ${v.steps}, Sleep ${v.sleep}hrs. Include warm-up, main workout and cool-down with duration.`
  },
  {
    id: 'sleep',
    label: 'Sleep Tips',
    icon: '😴',
    prompt: (v) => {
      const hrs = v.sleep > 0 ? v.sleep : 7;
      return `Give me ${hrs > 0 ? hrs + ' hours of sleep improvement tips' : 'general sleep improvement tips'} and an ideal bedtime routine with specific steps.`;
    }
  },
  {
    id: 'hydration',
    label: 'Hydration',
    icon: '💧',
    prompt: (v) => `I've had ${v.hydration}L of water today. Give me a personalized hydration plan with hourly timings for the rest of the day.`
  },
  {
    id: 'heart',
    label: 'Heart Health',
    icon: '❤️',
    prompt: (v) => `My heart rate is ${v.hr}bpm and I've walked ${v.steps} steps today. Analyze my heart health and give specific improvement tips.`
  },
];

const NAV_ITEMS = [
  { label: 'Dashboard', icon: LayoutDashboard },
  { label: 'Overview', icon: LayoutDashboard },
  { label: 'Wellness AI', icon: MessageSquare },
  { label: 'Activity', icon: Activity },
  { label: 'Health Vitals', icon: Thermometer },
  { label: 'Lab Reports', icon: Activity },
  { label: 'Health Analysis', icon: Zap },
  { label: 'Skin Health', icon: User },
  { label: 'Profile', icon: User },
  { label: 'Health Checkup', icon: ShieldAlert },
  { label: 'Health Routine', icon: FileText },
  { label: 'Settings', icon: Settings },
];

function Sidebar({ activeTab, onTabChange }) {
  return (
    <aside className="fixed top-0 left-0 h-screen w-64 bg-[#1e293b] border-r border-slate-800 flex flex-col py-8 px-5 shadow-2xl z-50">
      <div className="flex items-center gap-3 mb-10 px-2">
        <div className="w-9 h-9 bg-indigo-600 rounded-xl flex items-center justify-center shrink-0 shadow-lg shadow-indigo-500/20">
          <Heart size={18} className="text-white" />
        </div>
        <div>
          <p className="text-base font-black text-white leading-none tracking-tight">VitalAI</p>
          <p className="text-[10px] text-slate-500 font-bold uppercase mt-1 tracking-widest">Wellness</p>
        </div>
      </div>
      <nav className="flex-1 space-y-2">
        {NAV_ITEMS.map(({ label, icon: Icon }) => {
          const active = activeTab === label;
          return (
            <button
              key={label}
              onClick={() => onTabChange(label)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${active
                ? 'bg-indigo-600 text-white shadow-xl shadow-indigo-600/20'
                : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                }`}
            >
              <Icon size={18} />
              {label}
            </button>
          );
        })}
      </nav>
      <div className="mt-6 p-4 bg-[#0f172a] rounded-2xl border border-slate-800">
        <p className="text-xs font-black text-indigo-400 mb-1 uppercase tracking-widest">AI Status</p>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.5)]" />
          <p className="text-[10px] text-slate-500 font-bold">LLAMA 3.2 ONLINE</p>
        </div>
      </div>
    </aside>
  );
}

function MetricCard({ title, value, unit, icon: Icon, color, trend, trendValue }) {
  const getTheme = () => {
    switch (color) {
      case 'rose': return 'bg-rose-500/10 text-rose-500 border-rose-500/20';
      case 'amber': return 'bg-amber-500/10 text-amber-500 border-amber-500/20';
      case 'emerald': return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
      case 'sky': return 'bg-sky-500/10 text-sky-500 border-sky-500/20';
      default: return 'bg-indigo-500/10 text-indigo-500 border-indigo-500/20';
    }
  };
  return (
    <div className="bg-[#1e293b] rounded-3xl p-6 border border-slate-800 flex flex-col gap-4 hover:border-slate-700 transition-all group">
      <div className="flex justify-between items-start">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${getTheme()} border`}>
          <Icon size={20} />
        </div>
        <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${trend === 'up' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-rose-500/10 text-rose-500'}`}>
          {trend === 'up' ? '↑' : '↓'} {trendValue}%
        </span>
      </div>
      <div>
        <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest mb-1">{title}</p>
        <p className="text-2xl font-black text-white">
          {value} <span className="text-xs font-bold text-slate-600 uppercase ml-1">{unit}</span>
        </p>
      </div>
    </div>
  );
}

const apiClient = axios.create({ baseURL: API_BASE_URL, timeout: 120000 });

// ══════════════════════════════════════════════════════════════
// MAIN APP
// ══════════════════════════════════════════════════════════════
function App() {
  const [patientId, setPatientId] = useState('thaarani');
  const [wsConnected, setWsConnected] = useState(false);
  const [vitals, setVitals] = useState({
    hr: 72,
    steps: 0,
    sleep: 7.0,
    hydration: 1.5,
    breathing_rate: 16,
    hrv: 45,
    calories: 1350,
    energy: 90,
    recovery: 85,
    stress: 25,
    bp: '120/80',
    ai_insight: "Your vitals are steady. Stay hydrated today."
  });
  const [medReminders, setMedReminders] = useState([]);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [hcSyncStatus, setHcSyncStatus] = useState('idle');

  // ── FIX: These hooks must be INSIDE the component ──
  const [medReminderPopup, setMedReminderPopup] = useState(null);
  const medReminderTimersRef = useRef([]);

  const fetchProfile = async () => {
    try {
      const res = await apiClient.get(`/profile?user_id=${patientId}`);
      if (res.data) {
        setVitals(v => ({
          ...v,
          hr: res.data.hr || v.hr,
          breathing_rate: res.data.breathing_rate || res.data.breathingRate || v.breathing_rate,
          hydration: res.data.hydration || v.hydration,
          bp: res.data.bp || v.bp,
          hrv: res.data.hrv || v.hrv,
          sleep: res.data.sleep_hours || v.sleep,
          steps: res.data.steps_today || v.steps
        }));
        setUserProfile(prev => ({ ...(prev || {}), ...res.data }));
        setProfile(prev => ({ ...prev, ...res.data }));
        if (Array.isArray(res.data.reminders)) setMedReminders(res.data.reminders);
        if (res.data.latest_routine_result) setRoutineResult(res.data.latest_routine_result);
        if (res.data.latest_appointment) setAppointmentConfirmed(res.data.latest_appointment);
        if (res.data.latest_checkup_result) setSosResult(res.data.latest_checkup_result);
        if (res.data.latest_skin_analysis) setSkinAnalysisResult(res.data.latest_skin_analysis);
        if (res.data.latest_hrv_result) setHrvResult(res.data.latest_hrv_result);
        if (res.data.chat_history && Array.isArray(res.data.chat_history)) {
          const formattedHistory = res.data.chat_history.map(msg => ({
            role: msg.sender === 'user' ? 'user' : 'bot',
            text: msg.text,
            assistant_name: msg.sender === 'ai' ? 'Mobile Assistant' : undefined
          }));
          setMessages(prev => formattedHistory.length > prev.length ? formattedHistory : prev);
        }
      }
    } catch (err) {
      console.warn("Failed to fetch profile details on boot:", err);
    }
  };

  const syncProfilePatch = async (patch) => {
    if (!patientId) return;
    try {
      await apiClient.post('/dashboard/profile-sync', { user_id: patientId, patch });
    } catch (err) {
      console.warn("Profile sync failed:", err);
    }
  };

  const fetchHealthConnectSummary = async () => {
    try {
      setHcSyncStatus('syncing');
      const res = await apiClient.get(`/health-connect/summary?user_id=${patientId}`);
      if (res.data) {
        setVitals(v => ({
          ...v,
          steps: res.data.vitals.steps_today || v.steps,
          sleep: res.data.vitals.sleep_hours || v.sleep,
          hr: res.data.vitals.heart_rate || v.hr,
          calories: res.data.vitals.calories || v.calories
        }));
        setLastSyncTime(res.data.vitals.last_synced);
        if (res.data.medications && res.data.medications.medications) {
          setMedReminders(res.data.medications.medications);
        }
      }
      setHcSyncStatus('done');
    } catch (err) {
      console.warn("Failed to fetch Health Connect summary:", err);
      setHcSyncStatus('error');
    }
  };

  const toggleMedicationWeb = async (index) => {
    const updated = [...medReminders];
    updated[index].taken = !updated[index].taken;
    setMedReminders(updated);
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ source: 'web', user_id: patientId, reminders: updated }));
    }
    const takenCount = updated.filter(m => m.taken).length;
    const today = new Date().toDateString();
    try {
      await apiClient.post('/health-connect/medications', {
        user_id: patientId,
        date: today,
        medications: updated,
        taken_count: takenCount,
        total_count: updated.length
      });
    } catch (err) {
      console.error('Web Med Sync Error:', err);
    }
  };

  const [targetHydration, setTargetHydration] = useState(2.5);
  const [aiAnalysis, setAiAnalysis] = useState('Analyzing your vitals...');
  const [activeTab, setActiveTab] = useState('Dashboard');
  const [healthScore, setHealthScore] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [activeRecTab, setActiveRecTab] = useState('diet');
  const [activeTrendMetric, setActiveTrendMetric] = useState('hr');
  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem('vitalai_messages');
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    return [
      { role: 'bot', text: 'Hello! 👋 I am your AI Wellness & Medical Coach.\n\nI can help you with personalized diet plans, medical symptom checking, exercise routines, and heart health.\n\nTap a quick option below or ask me anything! 😊' }
    ];
  });
  const [selectedVoice, setSelectedVoice] = useState('female');
  const [chatSessionId, setChatSessionId] = useState(() => {
    try { return localStorage.getItem('vitalai_session_id') || `session_${Date.now()}`; } catch (e) { return `session_${Date.now()}`; }
  });
  const [inputText, setInputText] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isListeningGlobal, setIsListeningGlobal] = useState(false);
  const [isProcessingVoice, setIsProcessingVoice] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [activeChip, setActiveChip] = useState(null);
  const chatEndRef = useRef(null);
  const abortControllerRef = useRef(null);
  const [analysisRange, setAnalysisRange] = useState('daily');
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isScanningFood, setIsScanningFood] = useState(false);
  const [scannedFood, setScannedFood] = useState(null);
  const [lastMealImage, setLastMealImage] = useState(null);
  const [lastSkinImage, setLastSkinImage] = useState(null); // Separate state for skin images
  const [wsData, setWsData] = useState(null);
  const wsRef = useRef(null);
  const analysisVideoRef = useRef(null);
  const analysisCanvasRef = useRef(null);
  const skinVideoRef = useRef(null);
  const skinCanvasRef = useRef(null);
  const canvasRef = useRef(null);
  const videoRef = useRef(null);
  const isAnalyzingRef = useRef(false);
  const analysisStreamRef = useRef(null);
  const analysisIntervalRef = useRef(null);
  const ppgBufferRef = useRef([]);
  const skinStreamRef = useRef(null);

  const [workoutActive, setWorkoutActive] = useState(false);
  const [workoutSeconds, setWorkoutSeconds] = useState(0);
  const [sleepTimes, setSleepTimes] = useState({ bed: '22:30', wake: '06:30' });
  const [showHydrationPopup, setShowHydrationPopup] = useState(false);
  const [isScanningAnalysis, setIsScanningAnalysis] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [hrvResult, setHrvResult] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [isScanningSkin, setIsScanningSkin] = useState(false);
  const [skinAnalysisResult, setSkinAnalysisResult] = useState(null);
  const [skinCameraReady, setSkinCameraReady] = useState(false);
  const [debugLogs, setDebugLogs] = useState([]);
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(() => {
    try { 
      const hasProfile = localStorage.getItem('vitalai_profile');
      // FORCE REGISTRATION FOR TESTING - Uncomment this to always show registration
      return true; 
      // For production, use this:
      // return !hasProfile;
    } catch (e) { return true; }
  });
  const [onboardChoice, setOnboardChoice] = useState(null);
  const [enteredMedicalId, setEnteredMedicalId] = useState('');
  const [connectError, setConnectError] = useState('');
  const [showIdModal, setShowIdModal] = useState(false);
  const [generatedMedicalId, setGeneratedMedicalId] = useState('');
  const [userProfile, setUserProfile] = useState(null);
  const [dashboardReport, setDashboardReport] = useState(null);
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [healthHistory, setHealthHistory] = useState([]);
  const [dashboardNotifications, setDashboardNotifications] = useState([]);
  const [emergencyAlert, setEmergencyAlert] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [mentalInput, setMentalInput] = useState('');
  const [mentalLoading, setMentalLoading] = useState(false);
  const [profile, setProfile] = useState({
    name: '', age: 25, gender: 'Female', bp: '120/80', hr: 72, spo2: 98,
    occupation: 'Other', activity: 'Moderately Active',
    conditions: '', allergies: '', blood_group: 'O+',
    weight_loss_goal: 0, family_history: '',
    emergency_contact_name: '', emergency_contact_phone: '', relationship: '',
    city: '', state: '', country: '', pincode: '',
    email: '', phone_number: '',
    cuisine: 'South Indian', restrictions: 'Vegetarian',
    sleep_hours: 7.0, steps_today: 0
  });
  const [uploadLoading, setUploadLoading] = useState(false);
  const [integrations, setIntegrations] = useState({
    "cardiovascular": [
      { "name": "Pulse Oximeter", "protocol": "Bluetooth LE", "status": "stub" },
      { "name": "Heart Rate Strap", "protocol": "ANT+", "status": "stub" },
      { "name": "Holter Monitor", "protocol": "USB", "status": "stub" }
    ],
    "respiratory": [
      { "name": "Spirometer", "protocol": "Bluetooth", "status": "stub" },
      { "name": "CPAP Machine", "protocol": "Cloud API", "status": "stub" }
    ],
    "metabolic": [
      { "name": "CGM (Libre/Dexcom)", "protocol": "NFC/BLE", "status": "stub" },
      { "name": "Insulin Pump", "protocol": "Cloud", "status": "stub" }
    ],
    "smartwatch": [
      { "name": "Apple Watch", "protocol": "HealthKit", "status": "stub" },
      { "name": "Samsung Galaxy", "protocol": "Health Connect", "status": "stub" },
      { "name": "Fitbit", "protocol": "Web API", "status": "stub" }
    ]
  });
  const [sosData, setSosData] = useState({
    hr: 75, spo2: 98, bp_sys: 120, bp_dia: 80, temp: 36.6, rr: 18, glucose: 100,
    sleep_hours: 8, steps: 0, hydration_liters: 2
  });
  const [sosResult, setSosResult] = useState(null);
  const [isSosLoading, setIsSosLoading] = useState(false);
  const [routineData, setRoutineData] = useState({ q1: 'No', q2: 'No', q3: 'No', painLevel: 1, duration: '1 day', notes: '' });
  const [isAnalyzingRoutine, setIsAnalyzingRoutine] = useState(false);
  const [routineResult, setRoutineResult] = useState(null);
  const [appointmentForm, setAppointmentForm] = useState({ doctorId: 'dr_sharma', date: '', time: '10:00 AM', reason: '' });
  const [appointmentConfirmed, setAppointmentConfirmed] = useState(null);
  const [isBooking, setIsBooking] = useState(false);

  // ── TTS refs (not state, just module-level vars scoped inside component) ──
  const ttsAudioRef = useRef(null);
  const ttsQueueRef = useRef([]);
  const isSpeakingRef = useRef(false);

  const submitRoutine = async (e) => {
    if (e) e.preventDefault();
    setIsAnalyzingRoutine(true);
    setRoutineResult(null);
    try {
      const res = await apiClient.post('/records/analyze', {
        q1_chest_pain: routineData.q1,
        q2_headache_dizziness: routineData.q2,
        q3_fever: routineData.q3,
        q4_pain_level: parseInt(routineData.painLevel) || 1,
        q5_duration: routineData.duration,
        additional_notes: routineData.notes
      });
      setRoutineResult(res.data);
      await syncProfilePatch({ latest_routine_result: res.data });
      if (res.data.recommended_doctor_id) {
        setAppointmentForm(prev => ({ ...prev, doctorId: res.data.recommended_doctor_id }));
      }
    } catch (err) {
      console.error("Routine Submit Error:", err);
      alert("Failed to analyze routine. Is backend running?");
    } finally {
      setIsAnalyzingRoutine(false);
    }
  };

  const submitBooking = async (e) => {
    if (e) e.preventDefault();
    setIsBooking(true);
    try {
      const res = await apiClient.post('/records/book', {
        doctor_id: appointmentForm.doctorId,
        date: appointmentForm.date,
        time: appointmentForm.time,
        reason: appointmentForm.reason || 'General consultation'
      });
      setAppointmentConfirmed(res.data);
      await syncProfilePatch({ latest_appointment: res.data });
    } catch (err) {
      console.error("Booking Error:", err);
      alert("Failed to book appointment.");
    } finally {
      setIsBooking(false);
    }
  };

  const submitSOS = async (e) => {
    if (e) e.preventDefault();
    setIsSosLoading(true);
    setSosResult(null);
    try {
      const res = await apiClient.post('/alerts/vitals', { user_id: patientId, ...sosData });
      setSosResult(res.data);
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          source: 'web', user_id: patientId,
          checkup_result: { ...res.data, vitals: sosData }
        }));
      }
      await syncProfilePatch({
        latest_checkup_result: res.data,
        stats: {
          hr: Number(sosData.hr),
          bp: `${sosData.bp_sys}/${sosData.bp_dia}`,
          steps_today: Number(sosData.steps),
          sleep_hours: Number(sosData.sleep_hours),
          hydration: Number(sosData.hydration_liters)
        }
      });
      setVitals(v => ({
        ...v,
        hr: sosData.hr,
        bp: `${sosData.bp_sys}/${sosData.bp_dia}`,
        steps: sosData.steps,
        sleep: sosData.sleep_hours,
        hydration: sosData.hydration_liters
      }));
    } catch (err) {
      console.error("SOS Submit Error:", err);
      alert(`Analysis Failed: ${err.message}. Please ensure the backend is running.`);
    } finally {
      setIsSosLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadLoading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("user_id", userProfile?.name?.toLowerCase() || patientId);
    try {
      const response = await fetch("https://backend.mednevo.com/upload-medical-report", { method: "POST", body: formData });
      const data = await response.json();
      if (data.status === "success") {
        await fetchProfile();
        alert("Report uploaded and analyzed successfully!");
      } else {
        alert("Upload failed: " + data.message);
      }
    } catch (err) {
      console.error(err);
      alert("Upload failed. Is the backend running?");
    } finally {
      setUploadLoading(false);
    }
  };

  const handlePrescriptionUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadLoading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("user_id", patientId);
    try {
      const response = await fetch("https://backend.mednevo.com/upload-prescription", { method: "POST", body: formData });
      const data = await response.json();
      if (data.status === "success") {
        setMedReminders(data.medications);
        await fetchProfile();
        alert("Prescription scanned! Reminders are now set and synced with your mobile device.");
      } else {
        alert("Parsing failed: " + data.message);
      }
    } catch (err) {
      console.error(err);
      alert("Scan failed. Ensure backend is online and Groq API key is configured.");
    } finally {
      setUploadLoading(false);
    }
  };

  const addLog = (msg) => {
    console.log(msg);
    setDebugLogs(prev => [msg, ...prev].slice(0, 5));
  };

  const chartData = [
    { time: '06:00', hr: 65, steps: 200 },
    { time: '09:00', hr: 82, steps: 1200 },
    { time: '12:00', hr: 78, steps: 4500 },
    { time: '15:00', hr: 85, steps: 6800 },
    { time: '18:00', hr: 72, steps: 8420 },
    { time: '21:00', hr: 68, steps: 8900 },
  ];

  const weeklyData = [
    { day: 'Mon', steps: 7200, sleep: 6.5, hydration: 1.8, hr: 74 },
    { day: 'Tue', steps: 9100, sleep: 7.0, hydration: 2.1, hr: 71 },
    { day: 'Wed', steps: 5400, sleep: 6.0, hydration: 1.5, hr: 78 },
    { day: 'Thu', steps: 8420, sleep: 7.2, hydration: 1.5, hr: 72 },
    { day: 'Fri', steps: 11000, sleep: 8.0, hydration: 2.5, hr: 68 },
    { day: 'Sat', steps: 6300, sleep: 9.0, hydration: 2.0, hr: 70 },
    { day: 'Sun', steps: 4200, sleep: 7.5, hydration: 1.7, hr: 75 },
  ];

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  useEffect(() => {
    stopAllCameras();
    setIsScanningAnalysis(false);
    setIsScanningSkin(false);
  }, [activeTab]);

  // WebSocket
  useEffect(() => {
    if (!patientId) return;
    let active = true;
    let wsInstance = null;
    const connect = () => {
      if (!active) return;
      const hostname = window.location.hostname || '127.0.0.1';
      const wsUrl = 'wss://backend.mednevo.com/ws/vitals';
      wsInstance = new WebSocket(wsUrl);
      wsInstance.onopen = () => {
        if (!active) { wsInstance.close(); return; }
        setWsConnected(true);
        fetchProfile();
      };
      wsInstance.onmessage = (event) => {
        if (!active) return;
        const data = JSON.parse(event.data);
        setWsData(data);
        if (data.scores) setHealthScore(data.scores.overall);
        if (data.ai_insight) setAiAnalysis(data.ai_insight);
        if (data.vitals) {
          setVitals(v => ({
            ...v,
            hr: data.vitals.heart_rate?.value ?? v.hr,
            breathing_rate: data.vitals.breathing_rate?.value ?? v.breathing_rate,
            steps: data.vitals.steps?.value ?? v.steps,
            sleep: data.vitals.sleep?.value ?? v.sleep,
            hydration: data.vitals.hydration?.value ?? v.hydration,
            bp: data.vitals.bp?.value ?? data.vitals.blood_pressure?.value ?? v.bp,
            hrv: data.vitals.hrv?.value ?? v.hrv,
            calories: data.vitals.calories?.value ?? v.calories,
          }));
        } else if (data.source === 'mobile' && data.hr !== undefined) {
          setVitals(v => ({
            ...v,
            hr: data.hr ?? v.hr,
            sleep: data.sleep ?? v.sleep,
            steps: data.steps ?? v.steps,
            hydration: data.hydration ?? v.hydration,
            breathing_rate: data.breathing_rate ?? v.breathing_rate,
            hrv: data.hrv ?? v.hrv,
            bp: data.bp ?? v.bp,
            calories: data.calories ?? v.calories,
          }));
        }
        if (data.reminders && Array.isArray(data.reminders)) setMedReminders(data.reminders);
        if (data.chat_message) {
          setMessages(prev => {
            const senderRole = data.chat_message.sender === 'user' ? 'user' : 'bot';
            const exists = prev.some(m => m.text === data.chat_message.text && m.role === senderRole);
            if (exists) return prev;
            return [...prev, { role: senderRole, text: data.chat_message.text, assistant_name: data.chat_message.sender === 'ai' ? 'Mobile Assistant' : undefined }];
          });
        }
        if (data.routine_result) {
          setRoutineResult(data.routine_result);
          if (data.routine_result.recommended_doctor_id) setAppointmentForm(prev => ({ ...prev, doctorId: data.routine_result.recommended_doctor_id }));
        }
        if (data.appointment) setAppointmentConfirmed(data.appointment);
        if (data.checkup_result) {
          setSosResult(data.checkup_result);
          if (data.checkup_result.vitals) {
            setVitals(v => ({
              ...v,
              hr: data.checkup_result.vitals.hr ?? v.hr,
              bp: data.checkup_result.vitals.bp ?? v.bp,
              steps: data.checkup_result.vitals.steps ?? v.steps,
              sleep: data.checkup_result.vitals.sleep_hours ?? v.sleep,
              hydration: data.checkup_result.vitals.hydration_liters ?? v.hydration,
            }));
          }
        }
        if (data.hrv_result) setHrvResult(data.hrv_result);
        if (data.skin_analysis) setSkinAnalysisResult(data.skin_analysis);
        if (data.profile_patch) fetchProfile();
        if (data.profile_update) {
          setUserProfile(prev => ({ ...(prev || {}), ...data.profile_update }));
          setProfile(prev => ({ ...prev, ...data.profile_update }));
        }
        if (data.user_id) {
          const capitalized = data.user_id.charAt(0).toUpperCase() + data.user_id.slice(1);
          setUserProfile(prev => {
            if (!prev || prev.name !== capitalized) return { ...(prev || { age: 24, weight: 62, blood_group: 'O+' }), name: capitalized };
            return prev;
          });
        }
        if (data.voice_actions) {
          data.voice_actions.forEach(actionObj => {
            if (actionObj.action === 'navigate') setActiveTab(actionObj.target);
            else if (actionObj.action === 'trigger') { const el = document.getElementById(actionObj.target); if (el) el.click(); }
            else if (actionObj.action === 'set_profile') {
              const targetField = actionObj.target === 'full_name' ? 'name' : actionObj.target;
              setProfile(prev => ({ ...prev, [targetField]: actionObj.value }));
            }
          });
        }
      };
      wsInstance.onclose = () => {
        setWsConnected(false);
        setTimeout(connect, 3000);
      };
      wsInstance.onerror = () => setWsConnected(false);
      wsRef.current = wsInstance;
    };
    connect();
    return () => { active = false; if (wsInstance) wsInstance.close(); };
  }, [patientId]);

  useEffect(() => { if (patientId) fetchHealthConnectSummary(); }, [patientId]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ source: 'web', user_id: patientId, ...vitals }));
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [vitals]);

  useEffect(() => {
    let interval = null;
    if (workoutActive) interval = setInterval(() => setWorkoutSeconds(s => s + 1), 1000);
    return () => clearInterval(interval);
  }, [workoutActive]);

  useEffect(() => {
    const timer = setInterval(() => {
      setShowHydrationPopup(true);
      setTimeout(() => setShowHydrationPopup(false), 10000);
    }, 3600000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => { if (activeTab === 'Activity') fetchAnalysis(analysisRange); }, [activeTab]);
  useEffect(() => { if (activeTab === 'Lab Reports') fetchIntegrations(); }, [activeTab]);
  useEffect(() => () => { abortControllerRef.current?.abort(); }, []);

  useEffect(() => {
    if (activeTab !== 'Dashboard') return;
    apiClient.get('/Dashboard/Notifications', { params: { user_id: patientId } })
      .then(res => setDashboardNotifications(res.data.notifications || []))
      .catch(() => setDashboardNotifications([
        { title: 'Daily Check-in', message: 'Did you complete your exercise and medicine today?', type: 'reminder' },
        { title: 'Hydration Goal', message: 'Drink at least 2L of water today.', type: 'wellness' },
      ]));
    const bpSys = parseInt((vitals.bp || '120/80').split('/')[0]) || 120;
    apiClient.get('/alert/emergency', { params: { hr: vitals.hr || 72, bp: bpSys, breathing_rate: 18, sp02: 98, hydration: vitals.hydration || 1.5 } })
      .then(res => { if (res.data.status !== 'Normal') setEmergencyAlert(res.data); else setEmergencyAlert(null); })
      .catch(() => {});
  }, [activeTab, vitals]);

  useEffect(() => {
    if (activeTab === 'Health Checkup') {
      const bpParts = (vitals.bp || '120/80').split('/');
      setSosData(prev => ({
        ...prev,
        hr: vitals.hr || 75,
        bp_sys: parseInt(bpParts[0]) || 120,
        bp_dia: parseInt(bpParts[1]) || 80,
        steps: vitals.steps || 0,
        sleep_hours: vitals.sleep || 8,
        hydration_liters: vitals.hydration || 2
      }));
    }
  }, [activeTab, vitals]);

  // ── Medication reminder timers ──────────────────────────────
  useEffect(() => {
    medReminderTimersRef.current.forEach(t => clearTimeout(t));
    medReminderTimersRef.current = [];
    if (!medReminders || medReminders.length === 0) return;
    const now = new Date();
    medReminders.forEach((med, index) => {
      if (med.taken) return;
      const timeStr = med.time || "";
      const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
      if (!match) return;
      let hours = parseInt(match[1]);
      const minutes = parseInt(match[2]);
      const ampm = match[3].toUpperCase();
      if (ampm === "PM" && hours !== 12) hours += 12;
      if (ampm === "AM" && hours === 12) hours = 0;
      const medTime = new Date();
      medTime.setHours(hours, minutes, 0, 0);
      const diffMs = medTime.getTime() - now.getTime();
      const FIVE_MIN = 5 * 60 * 1000;
      if (diffMs > -FIVE_MIN && diffMs < 24 * 60 * 60 * 1000) {
        const delay = Math.max(0, diffMs);
        const timerId = setTimeout(() => {
          setMedReminderPopup({ index, name: med.name, time: med.time });
          if (Notification.permission === "granted") {
            new Notification("💊 Medication Reminder", {
              body: `Time to take ${med.name} — ${med.time}`,
              icon: "/favicon.ico",
              tag: `med-${index}`,
            });
          }
          speakResponse(`Reminder: Time to take ${med.name}. Please take your medication now.`, selectedVoice);
          setTimeout(() => setMedReminderPopup(null), 30000);
        }, delay);
        medReminderTimersRef.current.push(timerId);
      }
    });
    if (Notification.permission === "default") Notification.requestPermission();
    return () => { medReminderTimersRef.current.forEach(t => clearTimeout(t)); };
  }, [medReminders]);

  const speakInstant = (text, voiceHint = 'female') => {
    if (!text || !text.trim()) return;
    window.speechSynthesis.cancel();
    const clean = text.replace(/\*{1,3}(.*?)\*{1,3}/g, '$1').replace(/#{1,6}\s*/g, '').replace(/^\s*[-*]\s+/gm, '').replace(/\s+/g, ' ').trim();
    const snippet = (clean.match(/[^.!?\n]+[.!?\n]*/g) || [clean]).slice(0, 2).join(' ');
    const utter = new SpeechSynthesisUtterance(snippet);
    const voices = window.speechSynthesis.getVoices();
    const prefs = voiceHint === 'female'
      ? ['Samantha', 'Karen', 'Google UK English Female', 'Microsoft Zira', 'Google US English']
      : ['Daniel', 'Google UK English Male', 'Microsoft David'];
    const pick = prefs.map(p => voices.find(v => v.name.includes(p))).find(Boolean) || voices.find(v => v.lang.startsWith('en')) || voices[0];
    if (pick) utter.voice = pick;
    utter.rate = 1.1; utter.pitch = voiceHint === 'female' ? 1.1 : 0.95; utter.volume = 1;
    window.speechSynthesis.speak(utter);
  };

  const speakResponse = async (text, voiceHint = 'female') => {
    if (!text || !text.trim()) return;
    window.speechSynthesis.cancel();
    if (ttsAudioRef.current) { try { ttsAudioRef.current.pause(); } catch (e) {} ttsAudioRef.current = null; }
    ttsQueueRef.current = []; isSpeakingRef.current = false;
    speakInstant(text, voiceHint);
  };

  const getRuleEngineResults = (v = vitals) => {
    const br = v.breathing_rate || 16;
    const hrv = v.hrv || 45;
    const stress = v.hr < 70 ? 15 : (v.hr < 85 ? 35 : (v.hr < 100 ? 65 : 85));
    const energy = Math.min(100, Math.round((v.sleep / 8) * 100));
    const recovery = Math.min(100, Math.round((energy * 0.6) + (v.hr < 75 ? 40 : 20)));
    const riskScore = (stress > 70 ? 30 : 0) + (v.sleep < 6 ? 30 : 0) + (v.steps < 4000 ? 20 : 0) + (v.hydration < 1.5 ? 20 : 0);
    const riskLevel = riskScore < 30 ? 'Low' : (riskScore < 60 ? 'Moderate' : 'High');
    return {
      vitals: {
        heart_rate: { value: v.hr, status: v.hr < 60 ? 'Low' : (v.hr <= 100 ? 'Normal' : 'High') },
        breathing_rate: { value: br, status: br < 12 ? 'Low' : (br <= 20 ? 'Normal' : 'High') },
        hrv: { value: hrv, status: hrv > 50 ? 'Healthy' : 'Low' },
        sleep: { value: v.sleep, status: v.sleep < 6 ? 'Poor' : 'Good' },
        steps: { value: v.steps, status: v.steps < 4000 ? 'Sedentary' : 'Active' },
        hydration: { value: v.hydration, status: v.hydration < 1.5 ? 'Low' : 'Good' }
      },
      scores: { stress, energy, recovery },
      state: {
        heart: v.hr < 60 ? 'low' : (v.hr <= 100 ? 'normal' : 'high'),
        sleep: v.sleep < 6 ? 'insufficient' : (v.sleep <= 9 ? 'good' : 'overslept'),
        activity: v.steps < 4000 ? 'low' : (v.steps < 8000 ? 'moderate' : 'high'),
        breathing: br < 12 ? 'low' : (br <= 20 ? 'normal' : 'high'),
        riskLevel,
        riskScore
      },
      summary: [
        stress > 60 ? { text: 'Your stress is higher than usual', icon: '🟠', color: 'bg-amber-500/10' } : null,
        v.sleep < 6 ? { text: 'You slept less than recommended', icon: '🟣', color: 'bg-purple-500/10' } : null,
        v.steps < 5000 ? { text: 'Low activity level today', icon: '🔵', color: 'bg-sky-500/10' } : null,
        v.hydration < 2.0 ? { text: 'Hydration is below your target', icon: '💧', color: 'bg-indigo-500/10' } : null,
      ].filter(Boolean)
    };
  };

  const realTimeData = wsData || getRuleEngineResults(vitals);

  const fetchAIHealth = async (v = vitals) => {
    setLoading(true);
    try {
      const res = await apiClient.get('/health/monitor', { params: { hr: v.hr, sleep: v.sleep, steps: v.steps, hydration: v.hydration } });
      const data = res.data;
      setHealthData(data);
      setAiAnalysis(data.ai_explanation);
      const scores = data?.scores || { energy: 0, stress: 0, recovery: 0 };
      const overall = Math.round(((scores.energy || 0) + (100 - (scores.stress || 0)) + (scores.recovery || 0)) / 3);
      setHealthScore(overall);
    } catch (err) {
      setAiAnalysis(`Error: ${err.message}.`);
    } finally {
      setLoading(false);
    }
  };

  const fetchIntegrations = async () => {
    try {
      const res = await apiClient.get('/integrations/devices/status');
      setIntegrations(res.data);
    } catch (err) {
      console.error("Error fetching integrations:", err);
    }
  };

  const handleMentalChat = async () => {
    if (!mentalInput.trim()) return;
    setMentalLoading(true);
    try {
      const res = await apiClient.post('/chat/wellness', { message: mentalInput, session_id: 'dashboard_mental', vitals });
      setAiAnalysis(res.data.reply);
      setMentalInput('');
    } catch (err) {
      setAiAnalysis("Sorry, I'm unable to connect right now.");
    } finally {
      setMentalLoading(false);
    }
  };

  const fetchAnalysis = async (range) => {
    setAnalysisLoading(true);
    try {
      const res = await apiClient.post('/wellness/analysis', { range, vitals, session_id: `${patientId}_wellness` });
      setAnalysisData(res.data);
    } catch {
      setAnalysisData({
        range,
        diet_analysis: 'Your nutrition intake has been moderate. Focus on increasing protein and reducing refined carbs.',
        workout_analysis: "You've been active on most days. Your step count is good!",
        sleep_analysis: 'Sleep pattern is slightly irregular. Try to maintain a consistent 10:30pm bedtime.',
        hydration_analysis: "You're slightly under-hydrated most days. Set hourly water reminders.",
        overall_analysis: 'Overall health is good with a score of 72/100!',
        metrics: { diet: 65, workout: 78, sleep: 70, hydration: 55, overall: 72 }
      });
    } finally {
      setAnalysisLoading(false);
    }
  };

  const handleVitalChange = (key, value) => setVitals(prev => ({ ...prev, [key]: Number(value) }));

  const stopAllCameras = () => {
    stopAnalysisCamera();
    stopSkinCamera();
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(t => t.stop());
      videoRef.current.srcObject = null;
    }
  };

  const stopAnalysisCamera = () => {
    if (analysisStreamRef.current) { analysisStreamRef.current.getTracks().forEach(t => t.stop()); analysisStreamRef.current = null; }
    if (analysisIntervalRef.current) { clearInterval(analysisIntervalRef.current); analysisIntervalRef.current = null; }
    ppgBufferRef.current = [];
    setIsScanningAnalysis(false);
    setAnalysisProgress(0);
  };

  const startAnalysisCamera = async () => {
    stopAnalysisCamera();
    setCameraError(null);
    setHrvResult(null);
    ppgBufferRef.current = [];
    addLog('Initiating Health Analysis Camera...');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } }, audio: false });
      analysisStreamRef.current = stream;
      if (!analysisVideoRef.current) { stream.getTracks().forEach(t => t.stop()); setCameraError('Video element not ready.'); return; }
      analysisVideoRef.current.srcObject = stream;
      await new Promise((resolve, reject) => {
        const v = analysisVideoRef.current;
        v.onloadedmetadata = () => { v.play().then(resolve).catch(reject); };
        v.onerror = reject;
      });
      setIsScanningAnalysis(true);
      addLog('Analysis scanning started.');
      const SAMPLE_DURATION_MS = 10000;
      const SAMPLE_INTERVAL_MS = 33;
      const startTime = Date.now();
      analysisIntervalRef.current = setInterval(() => {
        const canvas = analysisCanvasRef.current;
        const video = analysisVideoRef.current;
        if (!canvas || !video || video.readyState < 2) return;
        canvas.width = video.videoWidth || 640;
        canvas.height = video.videoHeight || 480;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const cx = Math.floor(canvas.width / 2);
        const cy = Math.floor(canvas.height / 2);
        const regionSize = 60;
        const imageData = ctx.getImageData(cx - regionSize / 2, cy - regionSize / 2, regionSize, regionSize);
        let redSum = 0;
        for (let i = 0; i < imageData.data.length; i += 4) redSum += imageData.data[i];
        ppgBufferRef.current.push(redSum / (imageData.data.length / 4));
        const elapsed = Date.now() - startTime;
        setAnalysisProgress(Math.min(100, Math.round((elapsed / SAMPLE_DURATION_MS) * 100)));
        if (elapsed >= SAMPLE_DURATION_MS) {
          clearInterval(analysisIntervalRef.current);
          analysisIntervalRef.current = null;
          const hrv = computeHRVFromPPG(ppgBufferRef.current, 1000 / SAMPLE_INTERVAL_MS);
          setHrvResult(hrv);
          syncProfilePatch({ latest_hrv_result: hrv });
          addLog('Analysis complete.');
          stopAnalysisCamera();
        }
      }, SAMPLE_INTERVAL_MS);
    } catch (err) {
      addLog('Error: ' + err.message);
      setCameraError(`Camera error: ${err.message}`);
      stopAnalysisCamera();
    }
  };

  function computeHRVFromPPG(buffer, sampleRateHz) {
    if (buffer.length < 30) return { sdnn: '--', rmssd: '--', pnn50: '--' };
    const smoothed = buffer.map((v, i, a) => { const slice = a.slice(Math.max(0, i - 2), i + 3); return slice.reduce((s, x) => s + x, 0) / slice.length; });
    const mean = smoothed.reduce((s, v) => s + v, 0) / smoothed.length;
    const peaks = [];
    for (let i = 2; i < smoothed.length - 2; i++) {
      if (smoothed[i] > mean && smoothed[i] >= smoothed[i - 1] && smoothed[i] >= smoothed[i + 1] && smoothed[i] >= smoothed[i - 2] && smoothed[i] >= smoothed[i + 2]) peaks.push(i);
    }
    if (peaks.length < 4) return { sdnn: '--', rmssd: '--', pnn50: '--' };
    const msPerSample = 1000 / sampleRateHz;
    const rr = peaks.slice(1).map((p, i) => (p - peaks[i]) * msPerSample);
    const rrMean = rr.reduce((s, v) => s + v, 0) / rr.length;
    const sdnn = Math.round(Math.sqrt(rr.reduce((s, v) => s + (v - rrMean) ** 2, 0) / rr.length));
    const rmssd = Math.round(Math.sqrt(rr.slice(1).reduce((s, v, i) => s + (v - rr[i]) ** 2, 0) / (rr.length - 1)));
    const nn50 = rr.slice(1).filter((v, i) => Math.abs(v - rr[i]) > 50).length;
    const pnn50 = Math.round((nn50 / (rr.length - 1)) * 100);
    return { sdnn: `${sdnn} ms`, rmssd: `${rmssd} ms`, pnn50: `${pnn50}%` };
  }

  const startSkinCamera = async () => {
    console.log('📸 Starting skin camera...');
    addLog('Initiating Skin Camera...');
    setSkinAnalysisResult(null);
    setSkinCameraReady(false);
    setCameraError(null);
    setIsScanningSkin(true); // Set state FIRST so video element renders
    
    // Stop any existing stream
    if (skinStreamRef.current) { 
      skinStreamRef.current.getTracks().forEach(t => t.stop()); 
      skinStreamRef.current = null; 
    }
    
    // Wait for DOM to update
    setTimeout(async () => {
      try {
        console.log('🎥 Requesting camera permission...');
        
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          console.error('❌ Camera API not available');
          alert('Camera not supported in this browser.');
          setIsScanningSkin(false);
          return;
        }
        
        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            facingMode: 'user', 
            width: { ideal: 1280 }, 
            height: { ideal: 720 } 
          }, 
          audio: false 
        });
        
        console.log('✅ Camera stream obtained');
        skinStreamRef.current = stream;
        
        if (!skinVideoRef.current) { 
          console.error('❌ Video element not found');
          stream.getTracks().forEach(t => t.stop()); 
          setCameraError('Video element not ready.');
          setIsScanningSkin(false);
          return; 
        }
        
        skinVideoRef.current.srcObject = stream;
        
        await new Promise((resolve, reject) => {
          const v = skinVideoRef.current;
          v.onloadedmetadata = () => {
            console.log('✅ Video metadata loaded');
            v.play().then(resolve).catch(reject);
          };
          v.onerror = reject;
        });
        
        setSkinCameraReady(true);
        console.log('✅ Skin camera ready!');
        addLog('Skin camera ready.');
      } catch (err) {
        console.error('❌ Camera error:', err);
        addLog('Error: ' + err.message);
        
        let errorMsg = 'Camera access failed: ';
        if (err.name === 'NotAllowedError') {
          errorMsg += 'Permission denied. Please allow camera access.';
        } else if (err.name === 'NotFoundError') {
          errorMsg += 'No camera found.';
        } else {
          errorMsg += err.message;
        }
        
        setCameraError(errorMsg);
        alert(errorMsg + '\n\n💡 You can use the "Upload Image" option instead!');
        setIsScanningSkin(false);
      }
    }, 150);
  };

  const stopSkinCamera = () => {
    if (skinStreamRef.current) { skinStreamRef.current.getTracks().forEach(t => t.stop()); skinStreamRef.current = null; }
    setIsScanningSkin(false);
    setSkinCameraReady(false);
  };

  const captureSkin = async () => {
    console.log('📸 Capturing skin image...');
    addLog('Capturing skin...');
    const video = skinVideoRef.current;
    const canvas = skinCanvasRef.current;
    if (!video || !canvas || video.readyState < 2) {
      alert('Camera not ready. Please wait and try again.');
      return;
    }
    
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext('2d');
    
    // Mirror the image for selfie view
    ctx.save(); 
    ctx.translate(canvas.width, 0); 
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    ctx.restore();
    
    stopSkinCamera();
    addLog('Processing image...');
    
    canvas.toBlob(async (blob) => {
      if (!blob) {
        alert('Failed to capture image. Please try again.');
        return;
      }
      
      // Save image preview
      const imageUrl = URL.createObjectURL(blob);
      setLastSkinImage(imageUrl);
      console.log('✅ Skin image captured and saved to preview');
      
      const formData = new FormData();
      formData.append('file', blob, 'skin_capture.jpg');
      
      try {
        console.log('🚀 Sending to backend for skin analysis...');
        const res = await apiClient.post('/analysis/analyze/skin', formData);
        setSkinAnalysisResult(res.data);
        console.log('✅ Skin analysis complete:', res.data);
        await syncProfilePatch({ latest_skin_analysis: res.data });
        addLog('Skin analysis complete.');
      } catch (err) {
        console.error('❌ Skin analysis error:', err);
        addLog('API Error: ' + err.message);
        setSkinAnalysisResult({ 
          fatigue_level: 'Moderate', 
          redness: 'Low', 
          skin_texture: 'Normal',
          dark_circles: 'Mild', 
          acne_detected: false, 
          wellness_risk: 'Healthy', 
          recommendation: 'Skin appears healthy. API error occurred during processing.' 
        });
      }
    }, 'image/jpeg', 0.95);
  };

  const handleAction = (type) => {
    if (type === 'water') {
      setVitals(v => ({ ...v, hydration: Math.min(5, v.hydration + 0.25) }));
    } else if (type === 'meal') {
      setIsScanningFood(true);
      setTimeout(() => {
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
          navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
            .then(stream => { if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play(); } })
            .catch(err => console.error('Webcam Error: ', err));
        }
      }, 100);
    } else if (type === 'workout') {
      setWorkoutActive(!workoutActive);
      if (!workoutActive) setVitals(v => ({ ...v, hr: Math.min(180, v.hr + 15) }));
    } else if (type === 'sleep') {
      const hours = 8.0;
      setVitals(v => ({ ...v, sleep: hours }));
      alert(`Sleep logged: ${sleepTimes.bed} to ${sleepTimes.wake} (${hours} hrs)`);
    }
  };

  const captureFood = async () => {
    if (!videoRef.current || !canvasRef.current) {
      alert('Camera not ready. Please wait and try again.');
      return;
    }
    
    const canvas = canvasRef.current;
    const video = videoRef.current;
    
    if (!video.videoWidth || !video.videoHeight) {
      alert('Camera not initialized. Please wait for video to load.');
      return;
    }
    
    // Use FULL resolution for better quality
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    
    // Draw image with high quality
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(video, 0, 0);
    
    // Convert to blob with HIGH QUALITY (0.98 instead of 0.95)
    canvas.toBlob(async (blob) => {
      if (!blob) {
        alert('Failed to capture image. Please try again.');
        return;
      }
      
      console.log('📸 Captured image size:', blob.size, 'bytes');
      
      const imageUrl = URL.createObjectURL(blob);
      setLastMealImage(imageUrl);
      
      const formData = new FormData();
      formData.append('file', blob, 'food_capture.jpg');
      
      try {
        console.log('🚀 Sending to backend for analysis...');
        const res = await apiClient.post('/food/analyze', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        console.log('✅ Backend response:', res.data);
        
        // Set food data matching backend response
        setScannedFood({
          detected: res.data.detected,
          confidence: res.data.confidence,
          calories: res.data.nutrition?.calories || 0,
          nutrition: {
            carbs: res.data.nutrition?.carbs || 0,
            fat: res.data.nutrition?.fat || 0,
            protein: res.data.nutrition?.protein || 0,
            calories: res.data.nutrition?.calories || 0
          },
          verdict: res.data.verdict,
          benefits: res.data.benefits || [],
          concerns: res.data.concerns || [],
          tips: res.data.tips || []
        });
        
        console.log('✅ Food analysis complete:', res.data.detected);
        
        // Stop camera stream after capture
        const stream = video.srcObject;
        if (stream) {
          stream.getTracks().forEach(t => t.stop());
        }
      } catch (err) {
        console.error('❌ Food Analysis Error:', err);
        alert('Food analysis failed. Please try again or use the Upload option.');
        setIsScanningFood(false);
      }
    }, 'image/jpeg', 0.98); // HIGH QUALITY: 98% instead of 95%
  };

  const handleSend = async (textOverride) => {
    const textToSend = typeof textOverride === 'string' ? textOverride : inputText.trim();
    if (!textToSend || chatLoading) return;
    const userMsg = { id: Date.now(), sender: 'user', text: textToSend };
    setMessages(prev => [...prev, { role: 'user', text: textToSend }]);
    if (typeof textOverride !== 'string') setInputText('');
    setChatLoading(true);
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ source: 'web', user_id: patientId, chat_message: userMsg }));
    }
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    try {
      const res = await apiClient.post('/chat/wellness', { message: textToSend, session_id: chatSessionId, vitals, voice: selectedVoice }, { signal: abortControllerRef.current.signal });
      const botText = res.data?.reply || "I'm sorry, I encountered an issue. How else can I help?";
      const voiceHint = res.data?.browser_voice_hint || selectedVoice;
      const assistantName = res.data?.assistant_name || (selectedVoice === 'female' ? 'Aria' : 'Orion');
      setMessages(prev => [...prev, { role: 'bot', text: botText, assistant_name: assistantName, was_medical: res.data?.was_medical_query }]);
      const botMsg = { id: Date.now() + 1, sender: 'ai', text: botText };
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ source: 'web', user_id: patientId, chat_message: botMsg }));
      }
      speakResponse(botText, voiceHint);
    } catch (err) {
      if (err.name !== 'CanceledError') {
        const errorMsg = err.code === 'ECONNABORTED'
          ? 'The AI is taking longer than usual to respond. Please try again in a moment.'
          : 'Connection issue. Please ensure the Backend (8000) is running with Groq API configured.';
        setMessages(prev => [...prev, { role: 'bot', text: errorMsg }]);
        const botMsg = { id: Date.now() + 1, sender: 'ai', text: errorMsg };
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ source: 'web', user_id: patientId, chat_message: botMsg }));
        }
      }
    } finally {
      setChatLoading(false);
      setActiveChip(null);
    }
  };

  const handleChipClick = async (chip) => {
    console.log('🔵 Chip clicked:', chip.id, chip.action);
    
    if (chatLoading) return;
    setActiveChip(chip.id);
    
    // Handle special actions
    if (chip.action === 'scan_food') {
      console.log('📸 Opening food scanner...');
      setIsScanningFood(true);
      setScannedFood(null); // Reset previous results
      
      // Wait for DOM to render the video element
      setTimeout(async () => {
        console.log('🎥 Requesting camera access...');
        try {
          if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            console.error('❌ Camera API not available');
            alert('Camera not supported in this browser. Please use Chrome or Edge.');
            setIsScanningFood(false);
            return;
          }
          
          // Try to get camera with better settings
          const constraints = {
            video: {
              width: { ideal: 1280 },
              height: { ideal: 720 },
              facingMode: 'environment' // Back camera on mobile
            }
          };
          
          console.log('📹 Getting user media with constraints:', constraints);
          const stream = await navigator.mediaDevices.getUserMedia(constraints);
          console.log('✅ Camera stream obtained:', stream.getVideoTracks().length, 'tracks');
          
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            await videoRef.current.play();
            console.log('✅ Camera started successfully');
          } else {
            console.error('❌ videoRef.current is null');
          }
        } catch (err) {
          console.error('❌ Camera error:', err);
          let errorMsg = 'Camera access failed. ';
          
          if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
            errorMsg += 'Please allow camera permission in your browser settings.';
          } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
            errorMsg += 'No camera found on your device.';
          } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
            errorMsg += 'Camera is being used by another app. Please close other apps and try again.';
          } else {
            errorMsg += err.message || 'Unknown error.';
          }
          
          alert(errorMsg + '\n\n💡 You can use the "📤 Upload Food" button instead!');
          setIsScanningFood(false);
        }
      }, 150);
      return;
    }
    
    // Regular chat prompt
    if (chip.prompt) {
      handleSend(chip.prompt(vitals));
    }
  };

  const handleVoiceInput = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert("Browser doesn't support voice recognition."); return; }
    const r = new SR();
    r.lang = 'en-US';
    r.onstart = () => setIsListening(true);
    r.onend = () => setIsListening(false);
    r.onresult = (e) => { const t = e.results[0][0].transcript; setInputText(t); handleSend(t); };
    r.start();
  };

  const handleGlobalVoiceInput = async () => {
    if (isListeningGlobal) {
      if (mediaRecorderRef.current) mediaRecorderRef.current.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      mediaRecorder.ondataavailable = (event) => { if (event.data.size > 0) audioChunksRef.current.push(event.data); };
      mediaRecorder.onstop = async () => {
        setIsListeningGlobal(false);
        if (audioChunksRef.current.length === 0) { speakResponse("I didn't capture any audio.", selectedVoice); return; }
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        if (audioBlob.size < 500) return;
        setIsProcessingVoice(true);
        const formData = new FormData();
        formData.append('file', audioBlob, 'command.webm');
        try {
          const res = await apiClient.post('/voice/process-audio', formData);
          if (res.data && res.data.status === 'success' && res.data.actions) {
            for (const actionObj of res.data.actions) {
              if (actionObj.action === 'navigate') setActiveTab(actionObj.target);
              else if (actionObj.action === 'trigger') { const el = document.getElementById(actionObj.target); if (el) el.click(); }
              else if (actionObj.action === 'set_profile') setProfile(prev => ({ ...prev, [actionObj.target]: actionObj.value }));
              else if (actionObj.action === 'set_input') {
                const el = document.getElementById(actionObj.target) || document.querySelector(`[name="${actionObj.target}"]`);
                if (el) { el.value = actionObj.value; el.dispatchEvent(new Event('input', { bubbles: true })); }
              } else if (actionObj.action === 'logout') {
                setIsFirstTimeUser(true); setActiveTab('Overview');
                localStorage.removeItem('vitalai_profile');
              } else if (actionObj.action === 'speak') speakResponse(actionObj.reply, selectedVoice);
            }
          } else if (res.data && res.data.status === 'unrecognized') {
            speakResponse("I didn't quite catch that.", selectedVoice);
          }
        } catch (err) {
          console.error("Voice Error:", err);
          speakResponse("I'm sorry, I encountered an issue processing your voice command.", selectedVoice);
        } finally {
          setIsProcessingVoice(false);
        }
      };
      mediaRecorder.start();
      setIsListeningGlobal(true);
      setTimeout(() => { if (mediaRecorder.state === 'recording') mediaRecorder.stop(); }, 3500);
    } catch (err) {
      console.error("Microphone Access Error:", err);
      setIsListeningGlobal(false);
    }
  };

  const handleOnboarding = async () => {
    try {
      // Validate required fields
      if (!profile.name || !profile.email || !profile.phone_number) {
        alert('Please fill in all required fields: Name, Email, and Phone Number');
        return;
      }

      // Call NEW backend registration API
      const response = await apiClient.post('/auth/register', {
        full_name: profile.name,
        age: parseInt(profile.age) || 25,
        gender: profile.gender || 'Female',
        blood_group: profile.blood_group || 'O+',
        email: profile.email,
        phone_number: profile.phone_number,
        city: profile.city || '',
        state: profile.state || '',
        country: profile.country || 'India',
        pincode: profile.pincode || '',
        emergency_contact_name: profile.emergency_contact_name || '',
        emergency_contact_phone: profile.emergency_contact_phone || '',
        emergency_contact_relationship: profile.relationship || '',
        chronic_conditions: profile.conditions || '',
        allergies: profile.allergies || '',
        family_medical_history: profile.family_history || '',
        activity_level: profile.activity || 'Moderately Active',
        dietary_restrictions: profile.restrictions || 'Vegetarian',
        cuisine_preference: profile.cuisine || 'South Indian'
      });

      if (response.data && response.data.success) {
        // Use Medical ID from backend
        const medicalId = response.data.medical_id;
        setGeneratedMedicalId(medicalId);
        setPatientId(medicalId.toLowerCase());
        
        // Save complete profile
        const completeProfile = {
          ...profile,
          medical_id: medicalId
        };
        setUserProfile(completeProfile);
        
        // Save to localStorage
        localStorage.setItem('vitalai_profile', JSON.stringify(completeProfile));
        localStorage.setItem('vitalai_patient_id', medicalId.toLowerCase());
        localStorage.setItem('medical_id', medicalId);
        
        // Try to generate initial dashboard report
        try {
          const dashboardRes = await apiClient.post(`/dashboard/generate-report?user_id=${medicalId.toLowerCase()}`, null, {
            params: { 
              current_hr: vitals.hr, 
              current_sleep: vitals.sleep, 
              current_steps: vitals.steps 
            }
          });
          setDashboardReport(dashboardRes.data);
        } catch (reportErr) {
          console.warn("Dashboard report generation failed:", reportErr);
          // Continue anyway - report is optional
        }
        
        // Show success modal
        setShowIdModal(true);
        
        // Sync with WebSocket if connected
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            source: 'web',
            user_id: medicalId.toLowerCase(),
            profile_update: completeProfile
          }));
        }
      } else {
        throw new Error(response.data?.message || 'Registration failed');
      }
      
    } catch (err) {
      console.error("Registration error:", err);
      
      // Check if it's a duplicate email error
      if (err.response?.status === 400 && err.response?.data?.detail?.includes('already registered')) {
        alert('This email is already registered. Please use a different email or connect to your existing profile.');
        return;
      }
      
      // For other errors, show error and allow fallback
      alert(`Registration failed: ${err.response?.data?.detail || err.message}\n\nPlease check your internet connection and try again.`);
    }
  };

  const handleConnectProfile = async () => {
    if (!enteredMedicalId.trim()) { 
      setConnectError('Please enter a valid Medical ID'); 
      return; 
    }
    
    setConnectError('');
    
    try {
      const cleanId = enteredMedicalId.trim().toUpperCase(); // Medical IDs are uppercase
      
      // Call NEW backend profile API
      const res = await apiClient.get(`/auth/profile/${cleanId}`);
      
      if (res.data && res.data.success && res.data.profile) {
        const fetchedProfile = res.data.profile;
        
        // Map backend profile to frontend profile structure
        const completeProfile = {
          name: fetchedProfile.full_name || fetchedProfile.name,
          age: fetchedProfile.age || 25,
          gender: fetchedProfile.gender || 'Female',
          blood_group: fetchedProfile.blood_group || 'O+',
          email: fetchedProfile.email || '',
          phone_number: fetchedProfile.phone_number || '',
          city: fetchedProfile.city || '',
          state: fetchedProfile.state || '',
          country: fetchedProfile.country || 'India',
          pincode: fetchedProfile.pincode || '',
          emergency_contact_name: fetchedProfile.emergency_contact_name || '',
          emergency_contact_phone: fetchedProfile.emergency_contact_phone || '',
          relationship: fetchedProfile.emergency_contact_relationship || '',
          conditions: fetchedProfile.chronic_conditions || '',
          allergies: fetchedProfile.allergies || '',
          family_history: fetchedProfile.family_medical_history || '',
          activity: fetchedProfile.activity_level || 'Moderately Active',
          restrictions: fetchedProfile.dietary_restrictions || 'Vegetarian',
          cuisine: fetchedProfile.cuisine_preference || 'South Indian',
          medical_id: fetchedProfile.medical_id,
          // Default values for missing fields
          bp: '120/80',
          hr: 72,
          spo2: 98,
          occupation: 'Other',
          weight_loss_goal: 0,
          sleep_hours: 7.0,
          steps_today: 0
        };
        
        setUserProfile(completeProfile);
        setProfile(completeProfile);
        setPatientId(cleanId.toLowerCase());
        setGeneratedMedicalId(cleanId);
        
        // Save to localStorage
        localStorage.setItem('vitalai_profile', JSON.stringify(completeProfile));
        localStorage.setItem('vitalai_patient_id', cleanId.toLowerCase());
        localStorage.setItem('medical_id', cleanId);
        
        // Successfully connected - close onboarding
        setIsFirstTimeUser(false);
        setOnboardChoice(null);
        
        // Fetch latest health data if available
        try {
          await fetchProfile();
        } catch (e) {
          console.warn("Could not fetch additional profile data:", e);
        }
        
      } else {
        setConnectError('Medical ID not found. Please check your ID and try again, or create a new profile.');
      }
    } catch (err) {
      console.error("Connect profile error:", err);
      
      if (err.response?.status === 404) {
        setConnectError('Medical ID not found. Please register first or check your ID.');
      } else {
        setConnectError('Error connecting to server. Please check your connection and try again.');
      }
    }
  };

  useEffect(() => {
    try {
      const savedProfile = localStorage.getItem('vitalai_profile');
      const savedId = localStorage.getItem('vitalai_patient_id');
      if (savedProfile) {
        const parsed = JSON.parse(savedProfile);
        setUserProfile(parsed);
        setProfile(parsed);
        if (savedId) setPatientId(savedId);
        else if (parsed.name) setPatientId(parsed.name.trim().toLowerCase());
        setIsFirstTimeUser(false);
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    try { localStorage.setItem('vitalai_messages', JSON.stringify(messages)); } catch (e) {}
  }, [messages]);

  useEffect(() => {
    try { localStorage.setItem('vitalai_session_id', chatSessionId); } catch (e) {}
  }, [chatSessionId]);

  // ══════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════
  return (
    <div className="flex min-h-screen bg-[#0f172a] text-slate-200">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 ml-64 p-10">

        <header className="flex justify-between items-center mb-10">
          <div>
            <h1 className="text-4xl font-bold text-white mb-1">
              {new Date().getHours() < 12 ? 'Morning' : new Date().getHours() < 18 ? 'Afternoon' : 'Evening'}, {userProfile?.name || profile?.name || 'User'}
            </h1>
            <p className="text-slate-400 font-medium">Your health is our priority.</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex gap-3 items-center bg-[#1e293b] p-3 rounded-2xl border border-slate-700 shadow-sm">
              {[
                { key: 'hr', label: 'HR', min: 40, max: 180, step: 1, suffix: '' },
                { key: 'sleep', label: 'SLEEP', min: 0, max: 12, step: 0.5, suffix: 'h' },
                { key: 'hydration', label: 'WATER', min: 0, max: 5, step: 0.25, suffix: 'L' },
              ].map((item, i) => (
                <React.Fragment key={item.key}>
                  {i > 0 && <div className="h-8 w-px bg-slate-700" />}
                  <div className="flex flex-col gap-1">
                    <span className="text-[10px] uppercase font-bold text-slate-500">{item.label} ({vitals[item.key]}{item.suffix})</span>
                    <input type="range" min={item.min} max={item.max} step={item.step} value={vitals[item.key]}
                      onChange={(e) => handleVitalChange(item.key, e.target.value)}
                      className="h-1 w-20 cursor-pointer accent-indigo-500" />
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </header>

        {/* ── OVERVIEW TAB ── */}
        {activeTab === 'Overview' && (
          <>
            <div className="mb-6 p-4 bg-[#1e293b] rounded-2xl border border-amber-500/30 flex items-start gap-4">
              <Sparkles size={24} className="text-amber-400 shrink-0 mt-1" />
              <div>
                <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1">AI Wellness Coach</p>
                <p className="text-sm text-slate-200 italic font-semibold leading-relaxed">
                  "{vitals.ai_insight || "Your vitals are steady. Remember to take a deep breath and stay hydrated today."}"
                </p>
              </div>
            </div>
            <div className="mb-6 grid grid-cols-3 gap-6">
              {[
                { label: 'Energy', value: realTimeData?.scores?.energy || 90, color: 'text-amber-400', border: 'border-amber-500/20', bg: 'bg-amber-500/10' },
                { label: 'Recovery', value: realTimeData?.scores?.recovery || 85, color: 'text-indigo-400', border: 'border-indigo-500/20', bg: 'bg-indigo-500/10' },
                { label: 'Stress', value: realTimeData?.scores?.stress || 25, color: 'text-rose-400', border: 'border-rose-500/20', bg: 'bg-rose-500/10' },
              ].map(card => (
                <div key={card.label} className={`rounded-[2rem] p-6 shadow-sm border flex flex-col items-center text-center ${card.bg} ${card.border}`}>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">{card.label}</p>
                  <p className={`text-4xl font-black ${card.color}`}>{card.value}<span className="text-lg opacity-50">/100</span></p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-[#1e293b] rounded-3xl p-6 border border-slate-800 flex flex-col justify-between hover:border-rose-500/50 transition-colors">
                <div className="flex items-center gap-2 mb-4"><Heart size={18} className="text-rose-500" /><p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Heart Rate</p></div>
                <div>
                  <p className="text-4xl font-black text-white">{vitals.hr} <span className="text-sm font-bold text-slate-500">BPM</span></p>
                  <p className="text-xs font-bold text-slate-400 mt-1">HRV: <span className="text-white">{vitals.hrv}</span> ms</p>
                </div>
              </div>
              <div className="bg-[#1e293b] rounded-3xl p-6 border border-slate-800 flex flex-col justify-between hover:border-sky-500/50 transition-colors">
                <div className="flex items-center gap-2 mb-4"><Wind size={18} className="text-sky-500" /><p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Respiration</p></div>
                <div>
                  <p className="text-4xl font-black text-white">{vitals.breathing_rate} <span className="text-sm font-bold text-slate-500">BPM</span></p>
                  <p className="text-xs font-bold text-slate-400 mt-1">SpO2: <span className="text-white">98</span>%</p>
                </div>
              </div>
              <div className="bg-[#1e293b] rounded-3xl p-6 border border-slate-800 flex flex-col justify-between hover:border-emerald-500/50 transition-colors">
                <div className="flex items-center gap-2 mb-4"><Activity size={18} className="text-emerald-500" /><p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Steps Today</p></div>
                <div>
                  <p className="text-4xl font-black text-white">{vitals.steps}</p>
                  <div className="w-full h-1.5 bg-slate-800 rounded-full mt-2 overflow-hidden">
                    <div className="h-full bg-emerald-500" style={{ width: `${Math.min((vitals.steps / 10000) * 100, 100)}%` }} />
                  </div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase mt-2 text-right">Goal: 10,000</p>
                </div>
              </div>
              <div className="bg-[#1e293b] rounded-3xl p-6 border border-slate-800 flex flex-col justify-between hover:border-indigo-500/50 transition-colors">
                <div className="flex items-center gap-2 mb-4"><Moon size={18} className="text-indigo-500" /><p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Sleep Duration</p></div>
                <div>
                  <p className="text-4xl font-black text-white">{vitals.sleep} <span className="text-sm font-bold text-slate-500">hrs</span></p>
                  <div className="w-full h-1.5 bg-slate-800 rounded-full mt-2 overflow-hidden">
                    <div className="h-full bg-indigo-500" style={{ width: `${Math.min((vitals.sleep / 9) * 100, 100)}%` }} />
                  </div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase mt-2 text-right">Goal: 9 hrs</p>
                </div>
              </div>
            </div>
            {/* Medications Tracker */}
            <div className="bg-[#1e293b] rounded-3xl border border-slate-800 mb-8 overflow-hidden">
              <div className="p-6 border-b border-slate-800 flex justify-between items-center bg-[#0f172a]/50">
                <h3 className="text-lg font-black text-white uppercase tracking-tight">Daily Medications</h3>
                <div className="flex items-center gap-3">
                  <input type="file" id="prescriptionUpload" className="hidden" accept=".pdf,image/*,.docx" onChange={handlePrescriptionUpload} disabled={uploadLoading} />
                  <label htmlFor="prescriptionUpload" className={`cursor-pointer px-4 py-2 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all ${uploadLoading ? 'bg-slate-800 text-slate-500' : 'bg-amber-600/20 hover:bg-amber-600/30 text-amber-400 border border-amber-500/30 active:scale-95'}`}>
                    {uploadLoading ? "Scanning Rx..." : "📄 Upload Rx"}
                  </label>
                  <span className="text-xs font-bold text-slate-500">{new Date().toDateString()}</span>
                </div>
              </div>
              <div className="p-4 space-y-2">
                {medReminders.length === 0 ? (
                  <p className="text-center text-slate-500 font-bold py-6">No medications scheduled for today.</p>
                ) : (
                  medReminders.map((med, idx) => (
                    <div key={idx} className={`flex items-center justify-between p-4 rounded-2xl border transition-all ${med.taken ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-[#0f172a] border-slate-800 hover:border-slate-700'}`}>
                      <div>
                        <p className={`text-base font-bold ${med.taken ? 'text-emerald-400 line-through opacity-70' : 'text-white'}`}>{med.name}</p>
                        <p className="text-xs font-bold text-slate-500 mt-1">{med.time}</p>
                      </div>
                      <button onClick={() => toggleMedicationWeb(idx)}
                        className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${med.taken ? 'bg-emerald-500 border-emerald-500' : 'bg-transparent border-slate-600 hover:border-indigo-500'}`}>
                        {med.taken && <CheckCircle2 size={20} className="text-white" />}
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* DAILY LOGS SECTION - NEW */}
            <div className="bg-[#1e293b] rounded-3xl border border-slate-800 mb-8 overflow-hidden">
              <div className="p-6 border-b border-slate-800 bg-[#0f172a]/50">
                <h3 className="text-lg font-black text-white uppercase tracking-tight">Daily Activity Logs</h3>
                <p className="text-xs text-slate-500 font-bold uppercase tracking-widest mt-1">Track your wellness journey</p>
              </div>
              <div className="p-6 grid grid-cols-4 gap-4">
                {/* Log Water */}
                <button onClick={() => {
                  const glasses = prompt('How many glasses of water did you drink today? (1 glass ≈ 250ml)');
                  if (glasses && !isNaN(glasses)) {
                    const liters = (parseFloat(glasses) * 0.25).toFixed(1);
                    setVitals(v => ({ ...v, hydration: parseFloat(liters) }));
                    if (wsRef.current?.readyState === WebSocket.OPEN) {
                      wsRef.current.send(JSON.stringify({ hydration: parseFloat(liters), source: 'web', user_id: patientId }));
                    }
                    alert(`✅ Logged: ${liters}L of water`);
                  }
                }} className="bg-gradient-to-br from-sky-500/20 to-blue-500/20 hover:from-sky-500/30 hover:to-blue-500/30 border border-sky-500/30 rounded-2xl p-6 flex flex-col items-center gap-3 transition-all active:scale-95">
                  <div className="w-12 h-12 bg-sky-500/20 rounded-full flex items-center justify-center text-2xl">💧</div>
                  <div className="text-center">
                    <p className="text-sm font-black text-white">Log Water</p>
                    <p className="text-xs text-slate-400 mt-1">{vitals.hydration}L / 2.5L</p>
                  </div>
                </button>

                {/* Log Meal */}
                <button onClick={() => {
                  const meal = prompt('What did you eat? (Brief description)');
                  if (meal && meal.trim()) {
                    alert(`✅ Meal logged: ${meal}\n\nFor detailed nutrition analysis, use the Food Scanner in Wellness AI tab!`);
                  }
                }} className="bg-gradient-to-br from-emerald-500/20 to-green-500/20 hover:from-emerald-500/30 hover:to-green-500/30 border border-emerald-500/30 rounded-2xl p-6 flex flex-col items-center gap-3 transition-all active:scale-95">
                  <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center text-2xl">🍽️</div>
                  <div className="text-center">
                    <p className="text-sm font-black text-white">Log Meal</p>
                    <p className="text-xs text-slate-400 mt-1">Track nutrition</p>
                  </div>
                </button>

                {/* Log Workout */}
                <button onClick={() => {
                  const workout = prompt('Enter workout details:\nExamples: "30 min jogging", "1 hour gym", "Yoga session"');
                  if (workout && workout.trim()) {
                    const steps = vitals.steps + 3000; // Add estimated steps
                    setVitals(v => ({ ...v, steps }));
                    if (wsRef.current?.readyState === WebSocket.OPEN) {
                      wsRef.current.send(JSON.stringify({ steps, source: 'web', user_id: patientId }));
                    }
                    alert(`✅ Workout logged: ${workout}\n+3000 steps added!`);
                  }
                }} className="bg-gradient-to-br from-rose-500/20 to-pink-500/20 hover:from-rose-500/30 hover:to-pink-500/30 border border-rose-500/30 rounded-2xl p-6 flex flex-col items-center gap-3 transition-all active:scale-95">
                  <div className="w-12 h-12 bg-rose-500/20 rounded-full flex items-center justify-center text-2xl">💪</div>
                  <div className="text-center">
                    <p className="text-sm font-black text-white">Log Workout</p>
                    <p className="text-xs text-slate-400 mt-1">{vitals.steps} steps</p>
                  </div>
                </button>

                {/* Log Sleep */}
                <button onClick={() => {
                  const hours = prompt('How many hours did you sleep last night?');
                  if (hours && !isNaN(hours)) {
                    setVitals(v => ({ ...v, sleep: parseFloat(hours) }));
                    if (wsRef.current?.readyState === WebSocket.OPEN) {
                      wsRef.current.send(JSON.stringify({ sleep: parseFloat(hours), source: 'web', user_id: patientId }));
                    }
                    alert(`✅ Sleep logged: ${hours} hours`);
                  }
                }} className="bg-gradient-to-br from-indigo-500/20 to-purple-500/20 hover:from-indigo-500/30 hover:to-purple-500/30 border border-indigo-500/30 rounded-2xl p-6 flex flex-col items-center gap-3 transition-all active:scale-95">
                  <div className="w-12 h-12 bg-indigo-500/20 rounded-full flex items-center justify-center text-2xl">😴</div>
                  <div className="text-center">
                    <p className="text-sm font-black text-white">Log Sleep</p>
                    <p className="text-xs text-slate-400 mt-1">{vitals.sleep}h / 8h</p>
                  </div>
                </button>
              </div>
            </div>

            <div className="bg-[#0f172a] p-4 rounded-2xl border border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full ${hcSyncStatus === 'syncing' ? 'bg-amber-500 animate-pulse' : hcSyncStatus === 'error' ? 'bg-rose-500' : 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]'}`} />
                <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Google Health Connect</p>
              </div>
              <p className="text-[10px] font-bold text-slate-500">
                {hcSyncStatus === 'syncing' ? 'Syncing...' : lastSyncTime ? `Last synced: ${new Date(lastSyncTime).toLocaleTimeString()}` : 'Connected'}
              </p>
            </div>
          </>
        )}

        {/* ── WELLNESS AI TAB ── */}
        {activeTab === 'Wellness AI' && (
          <div className="bg-[#1e293b] rounded-[2rem] border border-slate-800 overflow-hidden shadow-2xl flex flex-col h-[750px]">
            <div className="p-8 pb-4 border-b border-slate-800">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-600/20">
                  <Sparkles size={24} className="text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-black text-white uppercase tracking-tight">AI Wellness Coach</h2>
                  <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">Powered by Llama 3.2 & Phi-3</p>
                </div>
              </div>
              <div className="flex items-center gap-3 mb-6 bg-[#0f172a]/50 p-4 rounded-2xl border border-slate-800">
                <div className="flex items-center gap-3">
                  <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Assistant:</span>
                  {[{ id: 'female', name: 'Aria', emoji: '👩‍⚕️' }, { id: 'male', name: 'Orion', emoji: '👨‍⚕️' }].map(v => (
                    <button key={v.id} onClick={() => setSelectedVoice(v.id)}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest border transition-all ${selectedVoice === v.id ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-slate-900 text-slate-500 border-slate-800 hover:border-indigo-500 hover:text-white'}`}>
                      <span>{v.emoji}</span><span>{v.name}</span>
                    </button>
                  ))}
                </div>
                <button onClick={() => { setChatSessionId(`session_${Date.now()}`); setMessages([{ role: 'bot', text: `New conversation started! I'm ${selectedVoice === 'female' ? 'Aria' : 'Orion'}, your health assistant. How can I help you today? 😊` }]); }}
                  className="ml-auto flex items-center gap-2 px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest border border-slate-700 text-slate-400 hover:border-emerald-500 hover:text-emerald-400 transition-all bg-slate-900">
                  + New Chat
                </button>
              </div>
              <div className="flex gap-2 flex-wrap mb-2">
                {WELLNESS_CHIPS.map(chip => (
                  <button key={chip.id} onClick={() => handleChipClick(chip)} disabled={chatLoading}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest border transition-all active:scale-95 disabled:opacity-50 ${activeChip === chip.id ? 'bg-indigo-600 text-white border-indigo-600 shadow-lg' : 'bg-[#0f172a] text-slate-400 border-slate-700 hover:border-indigo-500 hover:text-white'}`}>
                    <span>{chip.icon}</span><span>{chip.label}</span>
                  </button>
                ))}
                {/* Upload Food Image Button */}
                <label className="flex items-center gap-2 px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest border transition-all active:scale-95 cursor-pointer bg-[#0f172a] text-slate-400 border-slate-700 hover:border-emerald-500 hover:text-white">
                  <input 
                    type="file" 
                    className="hidden" 
                    accept="image/*" 
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      
                      const formData = new FormData();
                      formData.append('file', file);
                      
                      try {
                        setChatLoading(true);
                        const response = await apiClient.post('/food/analyze', formData, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                        });
                        
                        // Preview image
                        const reader = new FileReader();
                        reader.onload = (ev) => {
                          setLastMealImage(ev.target.result);
                        };
                        reader.readAsDataURL(file);
                        
                        // Set food data matching backend response structure
                        setScannedFood({
                          detected: response.data.detected,
                          confidence: response.data.confidence,
                          calories: response.data.nutrition?.calories || 0,
                          nutrition: {
                            carbs: response.data.nutrition?.carbs || 0,
                            fat: response.data.nutrition?.fat || 0,
                            protein: response.data.nutrition?.protein || 0,
                            calories: response.data.nutrition?.calories || 0
                          },
                          verdict: response.data.verdict,
                          benefits: response.data.benefits || [],
                          concerns: response.data.concerns || [],
                          tips: response.data.tips || []
                        });
                        
                        alert('✅ Food analysis complete! Check Health Vitals tab to see detailed results.');
                      } catch (err) {
                        console.error(err);
                        alert('Food analysis failed. Please try again.');
                      } finally {
                        setChatLoading(false);
                        e.target.value = ''; // Reset input
                      }
                    }}
                  />
                  <span>📤</span><span>Upload Food</span>
                </label>
                {/* DEBUG: Test Camera Button */}
                <button 
                  onClick={() => {
                    console.log('🔴 DEBUG: Manual camera trigger clicked');
                    console.log('Current isScanningFood state:', isScanningFood);
                    setIsScanningFood(true);
                    console.log('Set isScanningFood to TRUE');
                  }}
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest border transition-all active:scale-95 bg-rose-500/20 text-rose-400 border-rose-500/30 hover:bg-rose-500/30"
                >
                  <span>🔴</span><span>TEST CAM</span>
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-8 space-y-6 bg-[#0f172a]/50">
              {messages.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.role === 'bot' && (
                    <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center mr-3 mt-1 shrink-0">
                      <Sparkles size={18} className="text-white" />
                    </div>
                  )}
                  <div className={`max-w-[70%] p-5 rounded-[1.5rem] text-sm leading-relaxed font-medium whitespace-pre-line shadow-sm border ${msg.role === 'user' ? 'bg-indigo-600 text-white rounded-tr-none border-indigo-500' : 'bg-[#1e293b] text-slate-200 border-slate-800 rounded-tl-none'}`}>
                    {msg.assistant_name && <p className="text-[8px] font-black uppercase tracking-widest mb-1 opacity-50">{msg.assistant_name}</p>}
                    {msg.text}
                  </div>
                </div>
              ))}
              {chatLoading && (
                <div className="flex justify-start items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center shrink-0 animate-pulse"><Sparkles size={18} className="text-white" /></div>
                  <div className="bg-[#1e293b] border border-slate-800 text-slate-400 px-6 py-4 rounded-[1.5rem] rounded-tl-none text-xs font-bold uppercase tracking-widest">AI is thinking...</div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            <div className="p-8 border-t border-slate-800 bg-[#1e293b]">
              <div className="flex gap-3 bg-[#0f172a] p-2 rounded-2xl border border-slate-800 focus-within:border-indigo-500/50 transition-all">
                <button onClick={handleVoiceInput} className={`p-3 rounded-xl transition-all ${isListening ? 'bg-rose-500 text-white animate-pulse' : 'bg-slate-800 text-slate-500 hover:text-white'}`}>
                  {isListening ? <MicOff size={20} /> : <Mic size={20} />}
                </button>
                <input value={inputText} onChange={(e) => setInputText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !chatLoading) handleSend(); }}
                  className="flex-1 px-3 py-3 outline-none text-white font-bold text-sm bg-transparent placeholder:text-slate-600"
                  placeholder={chatLoading ? 'Coach is typing...' : 'Ask me anything about your health...'}
                  disabled={chatLoading} />
                <button onClick={() => handleSend()} disabled={chatLoading}
                  className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white px-8 py-3 rounded-xl font-black text-xs uppercase tracking-widest transition-all active:scale-95">
                  {chatLoading ? 'Wait' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── ACTIVITY TAB ── */}
        {activeTab === 'Activity' && (
          <div className="space-y-8">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h2 className="text-3xl font-black text-white uppercase tracking-tight">Activity Analytics</h2>
                <p className="text-xs text-slate-500 font-bold uppercase tracking-widest mt-1">AI-powered insights</p>
              </div>
              <div className="flex gap-2 bg-[#1e293b] p-1.5 rounded-2xl border border-slate-800 shadow-sm">
                {['daily', 'weekly', 'monthly'].map(range => (
                  <button key={range} onClick={() => { setAnalysisRange(range); fetchAnalysis(range); }}
                    className={`px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${analysisRange === range ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-500 hover:text-white'}`}>
                    {range}
                  </button>
                ))}
              </div>
            </div>
            {analysisLoading ? (
              <div className="flex items-center justify-center h-64 bg-[#1e293b] rounded-[2.5rem] border border-slate-800">
                <div className="text-center">
                  <div className="w-16 h-16 border-4 border-indigo-500/20 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-xs font-black text-slate-500 uppercase tracking-widest">Analyzing your patterns...</p>
                </div>
              </div>
            ) : analysisData && (
              <>
                <div className="grid grid-cols-5 gap-4">
                  {[
                    { label: 'Diet', icon: '🥗', key: 'diet', theme: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' },
                    { label: 'Workout', icon: '💪', key: 'workout', theme: 'bg-rose-500/10 text-rose-500 border-rose-500/20' },
                    { label: 'Sleep', icon: '😴', key: 'sleep', theme: 'bg-indigo-500/10 text-indigo-500 border-indigo-500/20' },
                    { label: 'Hydration', icon: '💧', key: 'hydration', theme: 'bg-sky-500/10 text-sky-500 border-sky-500/20' },
                    { label: 'Overall', icon: '❤️', key: 'overall', theme: 'bg-purple-500/10 text-purple-500 border-purple-500/20' },
                  ].map(item => (
                    <div key={item.label} className={`p-6 rounded-[2rem] border text-center ${item.theme}`}>
                      <div className="text-2xl mb-3">{item.icon}</div>
                      <p className="text-[10px] font-black uppercase tracking-widest mb-1 opacity-60">{item.label}</p>
                      <p className="text-3xl font-black">{analysisData.metrics?.[item.key] || 0}</p>
                      <p className="text-[10px] font-bold opacity-40">/100</p>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-[#1e293b] rounded-[2.5rem] p-8 border border-slate-800">
                    <h3 className="text-lg font-black text-white uppercase tracking-tight mb-6 flex items-center gap-3"><Zap size={20} className="text-amber-500" /> Weekly Activity</h3>
                    <div className="h-[220px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={weeklyData}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#0f172a" />
                          <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: '#475569', fontSize: 10, fontWeight: 'bold' }} />
                          <Tooltip cursor={{ fill: '#0f172a' }} contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '12px' }} />
                          <Bar dataKey="steps" fill="#6366f1" radius={[6, 6, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div className="bg-[#1e293b] rounded-[2.5rem] p-8 border border-slate-800">
                    <h3 className="text-lg font-black text-white uppercase tracking-tight mb-6 flex items-center gap-3"><Moon size={20} className="text-indigo-500" /> Sleep Trends</h3>
                    <div className="h-[220px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={weeklyData}>
                          <defs>
                            <linearGradient id="actSleepGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#818cf8" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#818cf8" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#0f172a" />
                          <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: '#475569', fontSize: 10, fontWeight: 'bold' }} />
                          <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '12px' }} />
                          <Area type="monotone" dataKey="sleep" stroke="#818cf8" strokeWidth={3} fill="url(#actSleepGrad)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── HEALTH VITALS TAB ── */}
        {activeTab === 'Health Vitals' && (
          <div className="space-y-6">
            {/* Top Row - Main Vitals */}
            <div className="grid grid-cols-3 gap-6">
              {[
                { label: 'Heart Rate', value: vitals.hr, unit: 'bpm', color: 'rose', icon: Heart },
                { label: 'Stress Score', value: realTimeData?.scores?.stress || 25, unit: '/100', color: 'amber', icon: Zap },
                { label: 'Energy', value: realTimeData?.scores?.energy || 90, unit: '/100', color: 'emerald', icon: Zap },
              ].map(stat => (
                <MetricCard key={stat.label} title={stat.label} value={stat.value} unit={stat.unit} icon={stat.icon} color={stat.color} trend="up" trendValue="3" />
              ))}
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-12 gap-6">
              {/* Left Column - Diet Details & Hydration */}
              <div className="col-span-7 space-y-6">
                {/* Food Scanner Card - Show when no food scanned */}
                {!scannedFood && (
                  <div className="bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 border-2 border-emerald-500/30 rounded-3xl p-8 text-center">
                    <div className="text-6xl mb-4">📸</div>
                    <h3 className="text-2xl font-black text-white uppercase tracking-tight mb-2">Analyze Your Meal</h3>
                    <p className="text-sm text-slate-400 mb-6">Capture or upload food to get instant nutrition analysis with AI</p>
                    <div className="flex gap-4 justify-center">
                      <button
                        onClick={async () => {
                          console.log('📸 Camera button clicked from Health Vitals');
                          setIsScanningFood(true);
                          setScannedFood(null);
                          
                          // Immediately try to start camera
                          setTimeout(async () => {
                            console.log('🎥 Attempting to start camera...');
                            try {
                              if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                                console.error('❌ Camera API not available');
                                alert('Camera not supported in this browser. Please use Chrome or Edge, or try the Upload option instead.');
                                setIsScanningFood(false);
                                return;
                              }
                              
                              console.log('📹 Requesting camera permission...');
                              const stream = await navigator.mediaDevices.getUserMedia({
                                video: {
                                  width: { ideal: 1280 },
                                  height: { ideal: 720 },
                                  facingMode: 'environment'
                                }
                              });
                              
                              console.log('✅ Camera stream obtained:', stream.active);
                              
                              if (videoRef.current) {
                                videoRef.current.srcObject = stream;
                                videoRef.current.onloadedmetadata = () => {
                                  console.log('✅ Video metadata loaded, playing...');
                                  videoRef.current.play();
                                };
                                await videoRef.current.play();
                                console.log('✅ Camera started successfully!');
                              } else {
                                console.error('❌ videoRef.current is null');
                                alert('Camera initialization failed. Please refresh and try again.');
                                stream.getTracks().forEach(t => t.stop());
                                setIsScanningFood(false);
                              }
                            } catch (err) {
                              console.error('❌ Camera error:', err);
                              let errorMsg = 'Camera access failed: ';
                              
                              if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                                errorMsg += 'Permission denied. Click the 🔒 icon in address bar and allow camera access.';
                              } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
                                errorMsg += 'No camera found. Please connect a webcam.';
                              } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
                                errorMsg += 'Camera is being used by another app. Please close other apps and try again.';
                              } else {
                                errorMsg += err.message || 'Unknown error.';
                              }
                              
                              alert(errorMsg + '\n\n💡 You can use the "Upload Image" button instead!');
                              setIsScanningFood(false);
                            }
                          }, 200);
                        }}
                        className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white rounded-2xl font-black uppercase tracking-widest text-sm shadow-lg transition-all active:scale-95"
                      >
                        <Camera size={24} />
                        <span>Open Camera</span>
                      </button>
                      <label className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white rounded-2xl font-black uppercase tracking-widest text-sm shadow-lg transition-all active:scale-95 cursor-pointer">
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            
                            const formData = new FormData();
                            formData.append('file', file);
                            
                            try {
                              setUploadLoading(true);
                              const response = await apiClient.post('/food/analyze', formData, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });
                              
                              const reader = new FileReader();
                              reader.onload = (ev) => setLastMealImage(ev.target.result);
                              reader.readAsDataURL(file);
                              
                              setScannedFood({
                                detected: response.data.detected,
                                confidence: response.data.confidence,
                                calories: response.data.nutrition?.calories || 0,
                                nutrition: {
                                  carbs: response.data.nutrition?.carbs || 0,
                                  fat: response.data.nutrition?.fat || 0,
                                  protein: response.data.nutrition?.protein || 0,
                                  calories: response.data.nutrition?.calories || 0
                                },
                                verdict: response.data.verdict,
                                benefits: response.data.benefits || [],
                                concerns: response.data.concerns || [],
                                tips: response.data.tips || []
                              });
                            } catch (err) {
                              console.error(err);
                              alert('Food analysis failed. Please try again.');
                            } finally {
                              setUploadLoading(false);
                              e.target.value = '';
                            }
                          }}
                        />
                        <span className="text-2xl">📤</span>
                        <span>Upload Image</span>
                      </label>
                    </div>
                  </div>
                )}
                
                {/* Diet Details Section */}
                {scannedFood && (
                  <div className="bg-[#1e293b] rounded-3xl border border-slate-800 overflow-hidden">
                    <div className="p-6 border-b border-slate-800 flex items-center justify-between bg-gradient-to-r from-emerald-500/10 to-transparent">
                      <h3 className="text-lg font-black text-white uppercase tracking-tight flex items-center gap-2">
                        🍽️ Diet Details
                      </h3>
                      <button onClick={() => setScannedFood(null)} className="text-xs text-slate-500 hover:text-white">Close</button>
                    </div>
                    <div className="p-6">
                      <div className="flex gap-6">
                        {lastMealImage && (
                          <img src={lastMealImage} alt="Meal" className="w-32 h-32 rounded-2xl object-cover border-2 border-slate-700" />
                        )}
                        <div className="flex-1">
                          <h4 className="text-xl font-black text-white mb-2">{scannedFood.detected}</h4>
                          <div className="flex items-center gap-2 mb-3">
                            <span className={`text-xs font-black px-3 py-1 rounded-full ${
                              scannedFood.verdict === 'healthy' ? 'bg-emerald-500/20 text-emerald-400' :
                              scannedFood.verdict === 'moderate' ? 'bg-amber-500/20 text-amber-400' :
                              'bg-rose-500/20 text-rose-400'
                            }`}>
                              {scannedFood.calories || 0} kcal
                            </span>
                            <span className="text-xs text-slate-500">Confidence: {Math.round((scannedFood.confidence || 0.7) * 100)}%</span>
                          </div>
                          <div className="grid grid-cols-3 gap-3">
                            <div className="bg-[#0f172a] rounded-xl p-3 border border-slate-800">
                              <p className="text-[10px] text-slate-500 font-bold uppercase">Carbs</p>
                              <p className="text-lg font-black text-white">{scannedFood.nutrition?.carbs || 0}g</p>
                            </div>
                            <div className="bg-[#0f172a] rounded-xl p-3 border border-slate-800">
                              <p className="text-[10px] text-slate-500 font-bold uppercase">Fats</p>
                              <p className="text-lg font-black text-white">{scannedFood.nutrition?.fat || 0}g</p>
                            </div>
                            <div className="bg-[#0f172a] rounded-xl p-3 border border-slate-800">
                              <p className="text-[10px] text-slate-500 font-bold uppercase">Protein</p>
                              <p className="text-lg font-black text-white">{scannedFood.nutrition?.protein || 0}g</p>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Benefits */}
                      {scannedFood.benefits && scannedFood.benefits.length > 0 && (
                        <div className="mt-4 p-4 bg-emerald-500/5 border border-emerald-500/20 rounded-2xl">
                          <p className="text-xs font-black text-emerald-400 uppercase mb-2 flex items-center gap-2">
                            ✓ Benefits
                          </p>
                          <ul className="space-y-1">
                            {scannedFood.benefits.map((benefit, i) => (
                              <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
                                <span className="text-emerald-500">•</span> {benefit}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Watch Out */}
                      {scannedFood.concerns && scannedFood.concerns.length > 0 && (
                        <div className="mt-4 p-4 bg-rose-500/5 border border-rose-500/20 rounded-2xl">
                          <p className="text-xs font-black text-rose-400 uppercase mb-2 flex items-center gap-2">
                            ⚠ Watch Out
                          </p>
                          <ul className="space-y-1">
                            {scannedFood.concerns.map((concern, i) => (
                              <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
                                <span className="text-rose-500">•</span> {concern}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Additional Tips */}
                      {scannedFood.tips && scannedFood.tips.length > 0 && (
                        <div className="mt-4 p-4 bg-indigo-500/5 border border-indigo-500/20 rounded-2xl">
                          <p className="text-xs font-black text-indigo-400 uppercase mb-2">💡 Tips</p>
                          <ul className="space-y-1">
                            {scannedFood.tips.map((tip, i) => (
                              <li key={i} className="text-sm text-slate-300">{tip}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Hydration Tracker */}
                <div className="bg-[#1e293b] rounded-3xl border border-slate-800 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-black text-white uppercase tracking-tight flex items-center gap-2">
                      💧 Hydration Tracker
                    </h3>
                    <button onClick={() => {
                      const add = prompt('Add water (glasses)? 1 glass = 250ml');
                      if (add && !isNaN(add)) {
                        const newHydration = vitals.hydration + (parseFloat(add) * 0.25);
                        setVitals(v => ({ ...v, hydration: newHydration }));
                        if (wsRef.current?.readyState === WebSocket.OPEN) {
                          wsRef.current.send(JSON.stringify({ hydration: newHydration, source: 'web', user_id: patientId }));
                        }
                      }
                    }} className="text-xs font-black text-sky-400 hover:text-sky-300">+ Add</button>
                  </div>
                  <div className="mb-3">
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="text-4xl font-black text-white">{vitals.hydration.toFixed(1)}L</span>
                      <span className="text-lg text-slate-500 font-bold">/ 2.5L</span>
                    </div>
                    <div className="w-full h-3 bg-slate-800 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-sky-500 to-blue-500 transition-all duration-500 relative"
                        style={{ width: `${Math.min((vitals.hydration / 2.5) * 100, 100)}%` }}
                      >
                        <div className="absolute inset-0 bg-white/20 animate-pulse" />
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-slate-400 font-bold">
                    {vitals.hydration >= 2.5 ? '✅ Daily goal achieved!' : 
                     vitals.hydration >= 2.0 ? '🎯 Almost there! Keep going.' :
                     vitals.hydration >= 1.5 ? '💪 Good progress. Drink more water.' :
                     '⚠️ Low hydration. Drink water now!'}
                  </p>
                  <p className="text-xs text-slate-500 mt-2">{Math.round((vitals.hydration / 2.5) * 100)}% of daily goal</p>
                </div>

                {/* Today's Trends Chart */}
                <div className="bg-[#1e293b] p-6 rounded-3xl border border-slate-800">
                  <h3 className="text-lg font-black text-white mb-4 uppercase tracking-tight">Today's Trends</h3>
                  <div className="h-[200px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData}>
                        <defs>
                          <linearGradient id="hrGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#f43f5e" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#0f172a" />
                        <XAxis dataKey="time" tick={{ fill: '#475569', fontSize: 10 }} />
                        <YAxis tick={{ fill: '#475569', fontSize: 10 }} />
                        <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '12px' }} />
                        <Area type="monotone" dataKey="hr" stroke="#f43f5e" fill="url(#hrGrad)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* Right Column - Quick Actions & Calories */}
              <div className="col-span-5 space-y-6">
                {/* Today's Calories */}
                <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-3xl p-6">
                  <h3 className="text-sm font-black text-purple-400 uppercase tracking-widest mb-4">Today's Calories</h3>
                  <div className="flex flex-col items-center">
                    <div className="relative w-40 h-40 mb-4">
                      <svg className="transform -rotate-90 w-40 h-40">
                        <circle cx="80" cy="80" r="70" stroke="#1e293b" strokeWidth="12" fill="none" />
                        <circle 
                          cx="80" cy="80" r="70" 
                          stroke="url(#calorieGrad)" 
                          strokeWidth="12" 
                          fill="none"
                          strokeDasharray={`${2 * Math.PI * 70}`}
                          strokeDashoffset={`${2 * Math.PI * 70 * (1 - ((scannedFood?.calories || 0) / 2000))}`}
                          className="transition-all duration-700"
                        />
                        <defs>
                          <linearGradient id="calorieGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#a855f7" />
                            <stop offset="100%" stopColor="#ec4899" />
                          </linearGradient>
                        </defs>
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <p className="text-4xl font-black text-white">{scannedFood?.calories || 0}</p>
                        <p className="text-xs text-slate-500 font-bold">kcal</p>
                      </div>
                    </div>
                    <p className="text-sm text-slate-400 font-bold">Goal: 2,000 kcal</p>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-[#1e293b] rounded-3xl border border-slate-800 p-6">
                  <h3 className="text-sm font-black text-white uppercase tracking-widest mb-4">Quick Actions</h3>
                  <div className="space-y-3">
                    <button onClick={() => {
                      const glasses = prompt('How many glasses of water? (1 glass = 250ml)');
                      if (glasses && !isNaN(glasses)) {
                        const newHydration = vitals.hydration + (parseFloat(glasses) * 0.25);
                        setVitals(v => ({ ...v, hydration: newHydration }));
                        if (wsRef.current?.readyState === WebSocket.OPEN) {
                          wsRef.current.send(JSON.stringify({ hydration: newHydration, source: 'web', user_id: patientId }));
                        }
                        alert(`✅ Added ${glasses} glasses of water!`);
                      }
                    }} className="w-full bg-gradient-to-r from-sky-500/20 to-blue-500/20 hover:from-sky-500/30 hover:to-blue-500/30 border border-sky-500/30 rounded-xl p-4 flex items-center gap-3 transition-all active:scale-95">
                      <div className="w-10 h-10 bg-sky-500/20 rounded-xl flex items-center justify-center text-lg">💧</div>
                      <div className="flex-1 text-left">
                        <p className="text-sm font-black text-white">LOG WATER</p>
                        <p className="text-xs text-slate-400">Track hydration</p>
                      </div>
                    </button>

                    <button onClick={() => setActiveTab('Wellness AI')} className="w-full bg-gradient-to-r from-emerald-500/20 to-green-500/20 hover:from-emerald-500/30 hover:to-green-500/30 border border-emerald-500/30 rounded-xl p-4 flex items-center gap-3 transition-all active:scale-95">
                      <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center text-lg">🍽️</div>
                      <div className="flex-1 text-left">
                        <p className="text-sm font-black text-white">LOG MEAL</p>
                        <p className="text-xs text-slate-400">Scan or enter food</p>
                      </div>
                    </button>

                    <button onClick={() => {
                      const workout = prompt('Enter workout details:');
                      if (workout && workout.trim()) {
                        const newSteps = vitals.steps + 3000;
                        setVitals(v => ({ ...v, steps: newSteps }));
                        if (wsRef.current?.readyState === WebSocket.OPEN) {
                          wsRef.current.send(JSON.stringify({ steps: newSteps, source: 'web', user_id: patientId }));
                        }
                        alert(`✅ Workout logged!\n+3000 steps added`);
                      }
                    }} className="w-full bg-gradient-to-r from-rose-500/20 to-pink-500/20 hover:from-rose-500/30 hover:to-pink-500/30 border border-rose-500/30 rounded-xl p-4 flex items-center gap-3 transition-all active:scale-95">
                      <div className="w-10 h-10 bg-rose-500/20 rounded-xl flex items-center justify-center text-lg">💪</div>
                      <div className="flex-1 text-left">
                        <p className="text-sm font-black text-white">LOG WORKOUT</p>
                        <p className="text-xs text-slate-400">Track exercise</p>
                      </div>
                    </button>

                    <button onClick={() => {
                      const hours = prompt('How many hours did you sleep?');
                      if (hours && !isNaN(hours)) {
                        setVitals(v => ({ ...v, sleep: parseFloat(hours) }));
                        if (wsRef.current?.readyState === WebSocket.OPEN) {
                          wsRef.current.send(JSON.stringify({ sleep: parseFloat(hours), source: 'web', user_id: patientId }));
                        }
                        alert(`✅ Sleep logged: ${hours} hours`);
                      }
                    }} className="w-full bg-gradient-to-r from-indigo-500/20 to-purple-500/20 hover:from-indigo-500/30 hover:to-purple-500/30 border border-indigo-500/30 rounded-xl p-4 flex items-center gap-3 transition-all active:scale-95">
                      <div className="w-10 h-10 bg-indigo-500/20 rounded-xl flex items-center justify-center text-lg">😴</div>
                      <div className="flex-1 text-left">
                        <p className="text-sm font-black text-white">LOG SLEEP</p>
                        <p className="text-xs text-slate-400">Track rest</p>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Health Summary */}
                <div className="bg-[#1e293b] p-6 rounded-3xl border border-slate-800">
                  <h3 className="text-lg font-black text-white mb-4 uppercase tracking-tight">Health Summary</h3>
                  <div className="space-y-3">
                    {(realTimeData?.summary || []).length > 0 ? realTimeData.summary.map((item, i) => (
                      <div key={i} className={`flex items-center gap-4 p-4 rounded-2xl ${item.color} border border-white/5`}>
                        <span className="text-lg">{item.icon}</span>
                        <p className="text-sm font-medium text-slate-300">{item.text}</p>
                      </div>
                    )) : (
                      <div className="flex flex-col items-center justify-center h-48 text-center opacity-50">
                        <CheckCircle2 size={40} className="text-emerald-500 mb-3" />
                        <p className="text-sm font-bold text-slate-400">Your vitals are looking excellent! Keep it up.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── LAB REPORTS TAB ── */}
        {activeTab === 'Lab Reports' && (
          <div className="space-y-8">
            <h2 className="text-3xl font-black text-white uppercase tracking-tight">Lab Reports & Analysis</h2>
            <div className="bg-[#1e293b] p-8 rounded-[2.5rem] border border-slate-800">
              <h3 className="text-sm font-black text-emerald-400 uppercase tracking-widest mb-6">Device Integration Ecosystem</h3>
              <div className="grid grid-cols-4 gap-6">
                {Object.entries(integrations).map(([category, devices]) => (
                  <div key={category} className="bg-[#0f172a]/50 border border-slate-800/50 p-6 rounded-3xl">
                    <div className="flex items-center justify-between mb-4 border-b border-slate-800 pb-3">
                      <h4 className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.2em]">{category.replace('_', ' ')}</h4>
                      <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                    </div>
                    <div className="space-y-4">
                      {devices.map((device, idx) => (
                        <div key={idx} className="flex justify-between items-start gap-3">
                          <div className="flex-1">
                            <p className="text-white text-xs font-black tracking-tight mb-0.5">{device.name}</p>
                            <p className="text-[9px] text-slate-500 font-bold uppercase">{device.protocol}</p>
                          </div>
                          <span className="shrink-0 text-[7px] font-black uppercase px-1.5 py-0.5 rounded-md bg-slate-800 text-slate-400 border border-slate-700">{device.status}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── HEALTH ANALYSIS TAB ── */}
        {activeTab === 'Health Analysis' && (
          <div className="space-y-8">
            <canvas ref={analysisCanvasRef} className="hidden" />
            <h2 className="text-3xl font-black text-white uppercase tracking-tight">Advanced Health Analysis</h2>
            
            {/* Upload Health Image Option */}
            <div className="bg-gradient-to-r from-rose-500/10 to-amber-500/10 border border-rose-500/20 rounded-3xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-black text-white mb-2">Upload Health Report or Image</h3>
                  <p className="text-sm text-slate-400">Upload any health-related document, report, or photo for AI analysis</p>
                </div>
                <div className="flex gap-3">
                  <input 
                    type="file" 
                    id="healthImageUpload" 
                    className="hidden" 
                    accept="image/*" 
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      
                      const formData = new FormData();
                      formData.append('file', file);
                      
                      try {
                        setUploadLoading(true);
                        
                        // Preview image
                        const reader = new FileReader();
                        reader.onload = (ev) => {
                          setLastMealImage(ev.target.result);
                        };
                        reader.readAsDataURL(file);
                        
                        // Try to analyze using available endpoints
                        // Using skin analysis as a general health analysis endpoint
                        try {
                          const response = await apiClient.post('/analysis/analyze/skin', formData, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                          });
                          
                          // Display results
                          alert(`✅ Health Analysis Complete!\n\nRecommendation: ${response.data.recommendation || 'Image analyzed successfully'}`);
                          setSkinAnalysisResult(response.data);
                        } catch (apiErr) {
                          // Fallback message
                          alert('✅ Image uploaded! For detailed analysis, please consult with a healthcare professional.');
                        }
                      } catch (err) {
                        console.error(err);
                        alert('Upload failed. Please try again.');
                      } finally {
                        setUploadLoading(false);
                        e.target.value = '';
                      }
                    }}
                  />
                  <label 
                    htmlFor="healthImageUpload" 
                    className={`px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest transition-all cursor-pointer ${
                      uploadLoading ? 'bg-slate-800 text-slate-500' : 'bg-rose-600 hover:bg-rose-500 text-white'
                    }`}
                  >
                    {uploadLoading ? '📤 Uploading...' : '📤 Upload Image'}
                  </label>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-8">
              <div className="col-span-2 bg-[#1e293b] rounded-[2.5rem] border border-slate-800 p-10">
                <h3 className="text-xl font-bold text-white mb-8">Webcam Pulse Scanner (PPG)</h3>
                <div className="aspect-video bg-slate-900 rounded-3xl border-2 border-dashed border-slate-700 flex flex-col items-center justify-center relative group overflow-hidden">
                  {cameraError && (
                    <div className="absolute inset-0 bg-rose-500/10 backdrop-blur-md flex flex-col items-center justify-center p-6 text-center z-10">
                      <p className="text-rose-500 font-black uppercase text-xs mb-2">Camera Access Failed</p>
                      <p className="text-white text-sm font-bold">{cameraError}</p>
                      <button onClick={() => setCameraError(null)} className="mt-4 text-[10px] font-black text-rose-500 uppercase tracking-widest underline">Dismiss</button>
                    </div>
                  )}
                  <video ref={analysisVideoRef} className={`w-full h-full object-cover ${isScanningAnalysis ? 'opacity-50 grayscale' : 'hidden'}`} playsInline muted autoPlay />
                  {isScanningAnalysis ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-10 bg-black/40 backdrop-blur-sm">
                      <div className="w-24 h-24 rounded-full border-4 border-indigo-500 border-t-transparent animate-spin mb-6" />
                      <p className="text-xl font-black text-white uppercase tracking-widest mb-2">Analyzing Pulse</p>
                      <div className="w-full max-w-md h-3 bg-white/10 rounded-full overflow-hidden">
                        <div className="h-full bg-indigo-500 transition-all duration-300" style={{ width: `${analysisProgress}%` }} />
                      </div>
                      <button onClick={stopAnalysisCamera} className="mt-6 text-[10px] font-black text-slate-400 uppercase tracking-widest underline hover:text-white">Cancel Scan</button>
                    </div>
                  ) : !cameraError && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <Camera size={48} className="text-slate-700 mb-4" />
                      <p className="text-slate-500 font-bold text-sm max-w-xs text-center">Place your finger on the camera for PPG analysis.</p>
                      <button onClick={startAnalysisCamera} className="mt-8 px-8 py-3 bg-indigo-600 text-white rounded-xl font-black uppercase text-xs tracking-widest hover:shadow-xl active:scale-95 transition-all">
                        Start Heart Scan
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="bg-[#1e293b] p-8 rounded-3xl border border-slate-800">
                <h4 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-6">HRV Metrics</h4>
                <div className="space-y-6">
                  {[
                    { label: 'SDNN', value: hrvResult?.sdnn || '--', color: 'text-emerald-500' },
                    { label: 'RMSSD', value: hrvResult?.rmssd || '--', color: 'text-indigo-500' },
                    { label: 'pNN50', value: hrvResult?.pnn50 || '--', color: 'text-sky-500' },
                  ].map(m => (
                    <div key={m.label} className="flex items-center justify-between">
                      <p className="text-lg font-black text-white">{m.label}</p>
                      <p className={`text-2xl font-black ${m.color}`}>{m.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── SKIN HEALTH TAB ── */}
        {activeTab === 'Skin Health' && (
          <div className="space-y-8">
            <canvas ref={skinCanvasRef} className="hidden" />
            <h2 className="text-3xl font-black text-white uppercase tracking-tight">AI Skin Analysis</h2>
            
            {/* Upload Option */}
            <div className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border border-indigo-500/20 rounded-3xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-black text-white mb-2">Analyze Your Skin</h3>
                  <p className="text-sm text-slate-400">Upload a selfie or use camera for AI-powered skin analysis</p>
                </div>
                <div className="flex gap-3">
                  <input 
                    type="file" 
                    id="skinImageUpload" 
                    className="hidden" 
                    accept="image/*" 
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      
                      const formData = new FormData();
                      formData.append('file', file);
                      
                      try {
                        setUploadLoading(true);
                        const response = await apiClient.post('/analysis/analyze/skin', formData, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                        });
                        setSkinAnalysisResult(response.data);
                        
                        // Preview image
                        const reader = new FileReader();
                        reader.onload = (ev) => {
                          setLastSkinImage(ev.target.result); // Use separate state for skin
                        };
                        reader.readAsDataURL(file);
                        
                        alert('✅ Skin analysis complete!');
                      } catch (err) {
                        console.error(err);
                        alert('Analysis failed. Please try again.');
                      } finally {
                        setUploadLoading(false);
                      }
                    }}
                  />
                  <label 
                    htmlFor="skinImageUpload" 
                    className={`px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest transition-all cursor-pointer ${
                      uploadLoading ? 'bg-slate-800 text-slate-500' : 'bg-indigo-600 hover:bg-indigo-500 text-white'
                    }`}
                  >
                    {uploadLoading ? '📤 Analyzing...' : '📤 Upload Image'}
                  </label>
                  <button 
                    onClick={isScanningSkin ? captureSkin : startSkinCamera} 
                    disabled={isScanningSkin && !skinCameraReady}
                    className="px-6 py-3 bg-purple-600 hover:bg-purple-500 text-white rounded-xl font-black uppercase text-xs tracking-widest transition-all disabled:opacity-50"
                  >
                    {isScanningSkin ? '📸 Capture' : '📷 Use Camera'}
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-6">
              {/* Camera Preview (if active) */}
              {isScanningSkin && (
                <div className="col-span-1 bg-[#1e293b] p-8 rounded-[2.5rem] border border-slate-800 flex flex-col items-center">
                  <div className="w-full aspect-square bg-slate-900 rounded-3xl border-2 border-dashed border-slate-700 flex flex-col items-center justify-center mb-6 overflow-hidden relative">
                    <video ref={skinVideoRef} className="w-full h-full object-cover" playsInline muted autoPlay style={{ transform: 'scaleX(-1)' }} />
                    <div className="absolute inset-0 border-4 border-indigo-500 animate-pulse pointer-events-none rounded-3xl" />
                  </div>
                  <button onClick={stopSkinCamera} className="w-full py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-xl font-black uppercase text-xs tracking-widest">
                    Cancel
                  </button>
                </div>
              )}
              
              {/* Image Preview (if uploaded) */}
              {lastSkinImage && !isScanningSkin && skinAnalysisResult && (
                <div className="col-span-1 bg-[#1e293b] p-8 rounded-[2.5rem] border border-slate-800 flex flex-col items-center">
                  <div className="w-full aspect-square bg-slate-900 rounded-3xl border-2 border-indigo-500/30 overflow-hidden mb-4">
                    <img src={lastSkinImage} alt="Analyzed" className="w-full h-full object-cover" />
                  </div>
                  <p className="text-xs text-slate-400 font-bold">Analyzed Image</p>
                </div>
              )}

              {/* Analysis Results */}
              <div className={`${isScanningSkin || (lastSkinImage && skinAnalysisResult) ? 'col-span-3' : 'col-span-4'} grid grid-cols-3 gap-6`}>
                {[
                  { label: 'Fatigue Signs', value: skinAnalysisResult?.fatigue_level || 'Pending', icon: Moon, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
                  { label: 'Redness', value: skinAnalysisResult?.redness || 'Pending', icon: Zap, color: 'text-amber-500', bg: 'bg-amber-500/10' },
                  { label: 'Skin Texture', value: skinAnalysisResult?.skin_texture || 'Pending', icon: Droplets, color: 'text-sky-500', bg: 'bg-sky-500/10' },
                  { label: 'Acne Detection', value: skinAnalysisResult?.acne_detected ? 'Detected' : (skinAnalysisResult ? 'Clear' : 'Pending'), icon: Sparkles, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
                  { label: 'Dark Circles', value: skinAnalysisResult?.dark_circles || 'Pending', icon: Moon, color: 'text-rose-500', bg: 'bg-rose-500/10' },
                  { label: 'Wellness Risk', value: skinAnalysisResult?.wellness_risk || 'Pending', icon: Heart, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
                ].map(card => (
                  <div key={card.label} className="bg-[#1e293b] p-6 rounded-3xl border border-slate-800">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${card.bg} ${card.color}`}><card.icon size={20} /></div>
                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">{card.label}</p>
                    <p className={`text-xl font-black ${card.color}`}>{card.value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Recommendation */}
            {skinAnalysisResult?.recommendation && (
              <div className="bg-[#1e293b] rounded-3xl border border-slate-800 p-8">
                <h3 className="text-lg font-black text-white mb-4 flex items-center gap-2">
                  <Sparkles size={20} className="text-indigo-400" /> AI Recommendation
                </h3>
                <p className="text-slate-300 leading-relaxed">{skinAnalysisResult.recommendation}</p>
              </div>
            )}
          </div>
        )}

        {/* ── PROFILE TAB ── */}
        {activeTab === 'Profile' && (
          <div className="space-y-8">
            <h2 className="text-3xl font-black text-white uppercase tracking-tight">My Profile</h2>
            {userProfile ? (
              <>
                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-[#1e293b] p-8 rounded-[2rem] border border-slate-800">
                    <h3 className="text-sm font-black text-indigo-400 uppercase tracking-widest mb-4">Personal Details</h3>
                    <div className="space-y-4">
                      <div>
                        <p className="text-[10px] text-indigo-400 uppercase tracking-widest font-black mb-1">Medical ID</p>
                        <div className="flex items-center gap-2">
                          <span className="text-emerald-400 font-mono font-black text-sm bg-[#0f172a] px-3 py-1.5 rounded-xl border border-slate-800">{patientId.toUpperCase()}</span>
                          <button onClick={() => { navigator.clipboard.writeText(patientId); alert('Medical ID copied!'); }} className="px-3 py-1 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold text-[10px] uppercase rounded-lg border border-slate-700">📋 Copy</button>
                        </div>
                      </div>
                      <div><p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">Name</p><p className="text-white font-bold">{userProfile.name || 'Not Provided'}</p></div>
                      <div><p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">Age & Gender</p><p className="text-white font-bold">{userProfile.age} | {userProfile.gender}</p></div>
                      <div><p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">Blood Group</p><p className="text-rose-400 font-black">{userProfile.blood_group || 'O+'}</p></div>
                    </div>
                  </div>
                  <div className="bg-[#1e293b] p-8 rounded-[2rem] border border-slate-800">
                    <h3 className="text-sm font-black text-emerald-400 uppercase tracking-widest mb-4">Health & Lifestyle</h3>
                    <div className="space-y-4">
                      <div><p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">Conditions</p><p className="text-white font-bold">{userProfile.conditions || 'None'}</p></div>
                      <div><p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">Activity Level</p><p className="text-white font-bold">{userProfile.activity}</p></div>
                      <div><p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">Diet</p><p className="text-white font-bold">{userProfile.cuisine} ({userProfile.restrictions})</p></div>
                    </div>
                  </div>
                </div>
                <div className="bg-[#1e293b] p-8 rounded-[2rem] border border-slate-800">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-sm font-black text-indigo-400 uppercase tracking-widest">Medical Reports</h3>
                    <div>
                      <input type="file" id="reportUpload" className="hidden" accept=".pdf,image/*,.docx" onChange={handleFileUpload} disabled={uploadLoading} />
                      <label htmlFor="reportUpload" className={`cursor-pointer px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest transition-all ${uploadLoading ? 'bg-slate-800 text-slate-500' : 'bg-indigo-600/20 hover:bg-indigo-600/30 text-indigo-400'}`}>
                        {uploadLoading ? "Analyzing..." : "+ Upload Report"}
                      </label>
                    </div>
                  </div>
                  {(!userProfile?.reports || userProfile.reports.length === 0) ? (
                    <p className="text-slate-500 font-medium text-sm text-center py-4">No medical reports uploaded yet.</p>
                  ) : (
                    <div className="space-y-4">
                      {userProfile.reports.map((report, idx) => (
                        <div key={idx} className="bg-[#0f172a] border border-slate-800 p-4 rounded-2xl flex flex-col gap-3">
                          <div className="flex justify-between items-center">
                            <p className="text-white font-bold">{report.original_name}</p>
                            <p className="text-xs text-slate-500 font-bold">{new Date(report.timestamp).toLocaleDateString()}</p>
                          </div>
                          <p className="text-slate-400 text-sm leading-relaxed border-t border-slate-800/50 pt-3">{report.analysis}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="bg-[#1e293b] p-10 rounded-[2rem] border border-slate-800 text-center">
                <p className="text-slate-400 font-bold mb-4">Your profile is not setup yet.</p>
                <button onClick={() => setIsFirstTimeUser(true)} className="px-6 py-3 bg-indigo-600 text-white rounded-2xl text-sm font-black uppercase tracking-widest">Setup Profile</button>
              </div>
            )}
          </div>
        )}

        {/* ── HEALTH CHECKUP TAB ── */}
        {activeTab === 'Health Checkup' && (
          <div className="space-y-8">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-3xl font-black text-white uppercase tracking-tight">Daily Health Checkup</h2>
                <p className="text-xs text-slate-500 font-bold uppercase tracking-widest mt-1">AI-Powered Risk Assessment</p>
              </div>
              <div className="bg-rose-600/10 border border-rose-500/20 px-6 py-3 rounded-2xl flex items-center gap-3">
                <ShieldAlert className="text-rose-500" />
                <span className="text-xs font-black text-rose-500 uppercase tracking-widest">Emergency System Active</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-8">
              <div className="bg-[#1e293b] p-10 rounded-[2.5rem] border border-slate-800">
                <h3 className="text-xl font-bold text-white mb-8">Input Today's Vitals</h3>
                <div className="grid grid-cols-2 gap-6">
                  {[
                    { key: 'hr', label: 'Heart Rate', unit: 'bpm' },
                    { key: 'spo2', label: 'SpO2', unit: '%' },
                    { key: 'bp_sys', label: 'BP Systolic', unit: 'mmHg' },
                    { key: 'bp_dia', label: 'BP Diastolic', unit: 'mmHg' },
                    { key: 'temp', label: 'Temperature', unit: '°C' },
                    { key: 'rr', label: 'Respiration', unit: 'br/min' },
                    { key: 'glucose', label: 'Glucose', unit: 'mg/dL' },
                    { key: 'sleep_hours', label: 'Sleep', unit: 'hrs' },
                    { key: 'steps', label: 'Steps', unit: 'steps' },
                    { key: 'hydration_liters', label: 'Hydration', unit: 'L' },
                  ].map(field => (
                    <div key={field.key} className="space-y-2">
                      <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{field.label}</label>
                      <div className="relative">
                        <input type="number" value={sosData[field.key]} onChange={(e) => setSosData({ ...sosData, [field.key]: parseFloat(e.target.value) })}
                          className="w-full bg-[#0f172a] border border-slate-800 rounded-xl px-4 py-3 text-white font-bold outline-none focus:ring-2 focus:ring-indigo-500" />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] font-black text-slate-600 uppercase">{field.unit}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <button onClick={submitSOS} disabled={isSosLoading}
                  className={`mt-10 w-full py-5 rounded-[1.5rem] font-black uppercase text-sm tracking-[0.2em] transition-all flex items-center justify-center gap-3 ${isSosLoading ? 'bg-slate-800 text-slate-500 cursor-wait' : 'bg-gradient-to-r from-indigo-600 to-indigo-700 text-white hover:shadow-xl active:scale-95'}`}>
                  {isSosLoading ? 'Analyzing...' : 'Run Risk Analysis'}
                </button>
              </div>
              <div>
                {!sosResult ? (
                  <div className="bg-[#1e293b]/50 border-2 border-dashed border-slate-800 rounded-[2.5rem] p-10 h-full flex flex-col items-center justify-center text-center opacity-50">
                    <ShieldAlert size={64} className="text-slate-700 mb-6" />
                    <p className="text-slate-500 font-bold max-w-xs">Submit your vitals to generate a real-time risk report.</p>
                  </div>
                ) : (
                  <div className={`p-10 rounded-[2.5rem] border ${sosResult.risk_score > 60 ? 'bg-rose-600/10 border-rose-500/30' : 'bg-[#1e293b] border-slate-800'}`}>
                    <div className="flex items-center justify-between mb-8">
                      <h3 className="text-xl font-bold text-white uppercase">Assessment Result</h3>
                      <span className={`px-4 py-2 rounded-full text-[10px] font-black uppercase border ${sosResult.risk_score > 60 ? 'bg-rose-500/10 text-rose-500 border-rose-500/20' : 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'}`}>
                        Risk: {sosResult.risk_score}%
                      </span>
                    </div>
                    {sosResult.risk_score >= 60 ? (
                      <div className="bg-rose-600 p-8 rounded-3xl text-white">
                        <div className="flex items-center gap-4 mb-4">
                          <PhoneCall size={24} />
                          <h4 className="text-lg font-black uppercase">⚠ High Risk Alert</h4>
                        </div>
                        <p className="text-sm mb-4">Risk score {sosResult.risk_score}%. Please seek medical attention.</p>
                        <a href={`tel:${userProfile?.emergency_contact_phone || ''}`}
                          className="block w-full py-3 bg-white text-rose-600 rounded-xl text-[10px] font-black uppercase tracking-widest text-center">
                          📞 Call Caretaker
                        </a>
                      </div>
                    ) : (
                      <div className="bg-emerald-500/10 border border-emerald-500/20 p-8 rounded-3xl">
                        <div className="flex items-center gap-4 mb-2">
                          <CheckCircle2 className="text-emerald-500" />
                          <h4 className="text-sm font-black text-white uppercase">All Clear — Normal Range</h4>
                        </div>
                        <p className="text-xs text-slate-400 font-medium">Your vitals are within safe margins. No emergency detected.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── HEALTH ROUTINE TAB ── */}
        {activeTab === 'Health Routine' && (
          <div className="space-y-8">
            <h2 className="text-3xl font-black text-white uppercase tracking-tight">Health Records Routine</h2>
            <div className="grid grid-cols-2 gap-8">
              <div className="bg-[#1e293b] p-8 rounded-[2rem] border border-slate-800">
                <h3 className="text-xl font-bold text-white mb-6">Daily Health Assessment</h3>
                <form onSubmit={submitRoutine} className="space-y-5">
                  {[
                    { key: 'q1', label: 'Any chest pain or difficulty breathing?' },
                    { key: 'q2', label: 'Severe headache or dizziness?' },
                    { key: 'q3', label: 'High fever or body chills?' },
                  ].map(q => (
                    <div key={q.key}>
                      <label className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 block">{q.label}</label>
                      <select value={routineData[q.key]} onChange={e => setRoutineData({ ...routineData, [q.key]: e.target.value })}
                        className="w-full bg-[#0f172a] text-white px-4 py-3 rounded-xl border border-slate-700 outline-none focus:border-indigo-500">
                        <option value="No">No — feeling fine</option>
                        <option value="Yes">Yes — experiencing this</option>
                      </select>
                    </div>
                  ))}
                  <div>
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 flex justify-between">
                      <span>Pain Level (1-10)</span><span className="text-indigo-400">{routineData.painLevel}</span>
                    </label>
                    <input type="range" min="1" max="10" value={routineData.painLevel}
                      onChange={e => setRoutineData({ ...routineData, painLevel: parseInt(e.target.value) })}
                      className="w-full accent-indigo-500" />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 block">Additional Notes</label>
                    <input type="text" value={routineData.notes} onChange={e => setRoutineData({ ...routineData, notes: e.target.value })}
                      placeholder="e.g. mild fatigue, skipped lunch..."
                      className="w-full bg-[#0f172a] text-white px-4 py-3 rounded-xl border border-slate-700 outline-none focus:border-indigo-500" />
                  </div>
                  <button type="submit" disabled={isAnalyzingRoutine}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-black uppercase tracking-widest py-4 rounded-xl transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2">
                    {isAnalyzingRoutine ? <Loader2 className="animate-spin" size={20} /> : <Zap size={20} />}
                    {isAnalyzingRoutine ? 'Analyzing...' : 'Analyze My Routine'}
                  </button>
                </form>
              </div>
              <div className="space-y-6">
                {routineResult ? (
                  <div className={`p-8 rounded-[2rem] border ${routineResult.severity === 'critical' ? 'bg-rose-500/10 border-rose-500/30' : 'bg-emerald-500/10 border-emerald-500/30'}`}>
                    <div className="flex items-center gap-4 mb-4">
                      {routineResult.severity === 'critical' ? <ShieldAlert size={32} className="text-rose-500" /> : <CheckCircle2 size={32} className="text-emerald-500" />}
                      <h3 className={`text-xl font-black uppercase ${routineResult.severity === 'critical' ? 'text-rose-500' : 'text-emerald-500'}`}>
                        {routineResult.severity === 'critical' ? 'Attention Required' : 'Status Normal'}
                      </h3>
                    </div>
                    <p className="text-slate-300 font-medium leading-relaxed">{routineResult.message}</p>
                  </div>
                ) : (
                  <div className="h-40 bg-[#1e293b] rounded-[2rem] border border-slate-800 border-dashed flex flex-col items-center justify-center text-slate-500 opacity-50">
                    <Activity size={32} className="mb-2" />
                    <p className="text-xs font-bold uppercase tracking-widest">Submit routine to view analysis</p>
                  </div>
                )}
                {appointmentConfirmed && (
                  <div className="bg-indigo-600/20 border border-indigo-500/30 p-8 rounded-[2rem] text-center">
                    <div className="w-16 h-16 bg-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4"><Phone className="text-white" size={32} /></div>
                    <h4 className="text-xl font-black text-white uppercase mb-2">Appointment Confirmed</h4>
                    <p className="text-sm text-indigo-200 mb-6">{appointmentConfirmed.message}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── SETTINGS TAB ── */}
        {activeTab === 'Settings' && (
          <div className="space-y-6">
            {/* Profile Settings */}
            <div className="bg-[#1e293b] p-10 rounded-[2rem] border border-slate-800">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-indigo-600/20 rounded-2xl flex items-center justify-center border border-indigo-500/30">
                  <User size={24} className="text-indigo-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-black text-white uppercase">Profile Settings</h2>
                  <p className="text-sm text-slate-400">Manage your health profile and preferences</p>
                </div>
              </div>

              {/* Current Profile Info */}
              {userProfile && (
                <div className="bg-[#0f172a] p-6 rounded-2xl border border-slate-800 mb-6">
                  <p className="text-xs font-black text-indigo-400 uppercase tracking-widest mb-3">Current Profile</p>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-[10px] text-slate-500 font-bold uppercase mb-1">Name</p>
                      <p className="text-sm text-white font-bold">{userProfile.name || 'Not set'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-500 font-bold uppercase mb-1">Medical ID</p>
                      <p className="text-sm text-indigo-400 font-mono font-bold">{generatedMedicalId || patientId || 'Not registered'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-500 font-bold uppercase mb-1">Email</p>
                      <p className="text-sm text-white font-bold">{userProfile.email || 'Not set'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Voice Settings */}
              <div className="mb-6">
                <p className="text-xs font-black text-emerald-400 uppercase tracking-widest mb-3">🎤 Voice Assistant</p>
                <div className="bg-[#0f172a] p-6 rounded-2xl border border-slate-800">
                  <label className="text-sm text-slate-400 font-bold mb-3 block">Select Voice</label>
                  <select 
                    value={selectedVoice} 
                    onChange={e => setSelectedVoice(e.target.value)}
                    className="w-full p-4 bg-[#1e293b] border border-slate-700 rounded-xl text-white font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="female">Female Voice</option>
                    <option value="male">Male Voice</option>
                  </select>
                </div>
              </div>

              {/* Data Management */}
              <div>
                <p className="text-xs font-black text-amber-400 uppercase tracking-widest mb-3">⚙️ Data Management</p>
                <div className="space-y-3">
                  {/* Reset Registration Button */}
                  <div className="bg-rose-500/10 p-6 rounded-2xl border border-rose-500/30">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <p className="text-sm font-black text-white mb-1">Reset Profile & Re-register</p>
                        <p className="text-xs text-slate-400">Clear your current profile and go through registration again. This will delete all local data including your Medical ID.</p>
                      </div>
                      <button
                        onClick={() => {
                          if (window.confirm('⚠️ Are you sure you want to reset your profile?\n\nThis will:\n- Clear your local profile data\n- Remove your Medical ID\n- Clear all chat history\n- Show registration page again\n\nYour data on the backend will remain safe.')) {
                            // Clear all localStorage
                            localStorage.removeItem('vitalai_profile');
                            localStorage.removeItem('vitalai_patient_id');
                            localStorage.removeItem('medical_id');
                            localStorage.removeItem('vitalai_messages');
                            localStorage.removeItem('vitalai_session_id');
                            
                            // Reset state
                            setUserProfile(null);
                            setProfile({
                              name: '', age: 25, gender: 'Female', bp: '120/80', hr: 72, spo2: 98,
                              occupation: 'Other', activity: 'Moderately Active',
                              conditions: '', allergies: '', blood_group: 'O+',
                              weight_loss_goal: 0, family_history: '',
                              emergency_contact_name: '', emergency_contact_phone: '', relationship: '',
                              city: '', state: '', country: '', pincode: '',
                              email: '', phone_number: '',
                              cuisine: 'South Indian', restrictions: 'Vegetarian',
                              sleep_hours: 7.0, steps_today: 0
                            });
                            setGeneratedMedicalId('');
                            setPatientId('');
                            setMessages([
                              { role: 'bot', text: 'Hello! 👋 I am your AI Wellness & Medical Coach.\n\nI can help you with personalized diet plans, medical symptom checking, exercise routines, and heart health.\n\nTap a quick option below or ask me anything! 😊' }
                            ]);
                            
                            // Show registration page
                            setIsFirstTimeUser(true);
                            setOnboardChoice(null);
                            setShowIdModal(false);
                            
                            alert('✅ Profile reset successfully!\n\nYou will now see the registration page.');
                          }
                        }}
                        className="px-6 py-3 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-black text-xs uppercase tracking-widest transition-all active:scale-95 shrink-0"
                      >
                        🔄 Reset & Re-register
                      </button>
                    </div>
                  </div>

                  {/* Clear Chat History */}
                  <div className="bg-amber-500/10 p-6 rounded-2xl border border-amber-500/30">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <p className="text-sm font-black text-white mb-1">Clear Chat History</p>
                        <p className="text-xs text-slate-400">Remove all conversation history with the AI assistant. Your profile data will remain intact.</p>
                      </div>
                      <button
                        onClick={() => {
                          if (window.confirm('Clear all chat history?')) {
                            setMessages([
                              { role: 'bot', text: 'Hello! 👋 I am your AI Wellness & Medical Coach.\n\nI can help you with personalized diet plans, medical symptom checking, exercise routines, and heart health.\n\nTap a quick option below or ask me anything! 😊' }
                            ]);
                            localStorage.removeItem('vitalai_messages');
                            alert('✅ Chat history cleared!');
                          }
                        }}
                        className="px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-bold text-xs uppercase transition-all active:scale-95 shrink-0"
                      >
                        🗑️ Clear History
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* App Information */}
            <div className="bg-[#1e293b] p-10 rounded-[2rem] border border-slate-800">
              <p className="text-xs font-black text-sky-400 uppercase tracking-widest mb-4">ℹ️ App Information</p>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-4 bg-[#0f172a] rounded-xl border border-slate-800">
                  <span className="text-sm text-slate-400 font-bold">Version</span>
                  <span className="text-sm text-white font-bold">2.0.0</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-[#0f172a] rounded-xl border border-slate-800">
                  <span className="text-sm text-slate-400 font-bold">Backend Status</span>
                  <span className={`text-sm font-bold ${wsConnected ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {wsConnected ? '🟢 Connected' : '🔴 Disconnected'}
                  </span>
                </div>
                <div className="flex justify-between items-center p-4 bg-[#0f172a] rounded-xl border border-slate-800">
                  <span className="text-sm text-slate-400 font-bold">AI Model</span>
                  <span className="text-sm text-white font-bold">Llama 3.3 70B</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── DASHBOARD TAB ── */}
        {activeTab === 'Dashboard' && (
          <div className="space-y-6">
            <div className="grid grid-cols-12 gap-6">
              {/* AI Health Summary */}
              <div className="col-span-4 bg-[#1e293b] rounded-3xl p-6 border border-slate-800">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-black text-white uppercase tracking-widest flex items-center gap-2"><Sparkles size={16} className="text-indigo-400" />AI Health Summary</h3>
                </div>
                <div className="space-y-3">
                  {[
                    { icon: '💚', color: 'bg-emerald-500/10 border-emerald-500/20', text: 'Your health looks stable today.', sub: 'Blood pressure and heart rate are in good range.' },
                    { icon: '🟡', color: 'bg-amber-500/10 border-amber-500/20', text: vitals.hydration < 1.5 ? 'Hydration is below target.' : 'Hydration looks good today.', sub: `${vitals.hydration}L consumed. Target: 2.5L` },
                    { icon: '💜', color: 'bg-purple-500/10 border-purple-500/20', text: vitals.sleep < 6 ? 'You slept less than recommended.' : `Sleep: ${vitals.sleep} hrs — Good`, sub: vitals.sleep < 6 ? 'Try to get at least 7-8 hours.' : 'Sleep score improved this week.' },
                  ].map((item, i) => (
                    <div key={i} className={`p-4 rounded-2xl border ${item.color}`}>
                      <div className="flex items-start gap-3">
                        <span className="text-lg">{item.icon}</span>
                        <div className="flex-1">
                          <p className="text-xs font-bold text-white">{item.text}</p>
                          <p className="text-[10px] text-slate-400 mt-0.5">{item.sub}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Vitals */}
              <div className="col-span-5 bg-[#1e293b] rounded-3xl p-6 border border-slate-800">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-black text-white uppercase tracking-widest">Quick Vitals Overview</h3>
                  {wsConnected ? (
                    <span className="text-[9px] font-black text-emerald-400 px-2 py-1 bg-emerald-500/10 rounded-full flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse inline-block" />Live
                    </span>
                  ) : (
                    <span className="text-[9px] font-black text-rose-400 px-2 py-1 bg-rose-500/10 rounded-full">Offline</span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Heart Rate', value: `${vitals.hr}`, unit: 'bpm', status: vitals.hr <= 100 ? 'Normal' : 'High', color: vitals.hr <= 100 ? 'text-emerald-400' : 'text-rose-400' },
                    { label: 'Blood Pressure', value: vitals.bp || '120/80', unit: 'mmHg', status: 'Normal', color: 'text-emerald-400' },
                    { label: 'Steps Today', value: (vitals.steps || 0).toLocaleString(), unit: 'steps', status: vitals.steps >= 8000 ? 'Goal Met' : 'In Progress', color: vitals.steps >= 8000 ? 'text-emerald-400' : 'text-amber-400' },
                    { label: 'Sleep', value: `${Math.floor(vitals.sleep)}h ${Math.round((vitals.sleep % 1) * 60)}m`, unit: '', status: vitals.sleep >= 7 ? 'Good' : 'Poor', color: vitals.sleep >= 7 ? 'text-emerald-400' : 'text-rose-400' },
                  ].map(v => (
                    <div key={v.label} className="bg-[#0f172a] p-4 rounded-2xl border border-slate-800">
                      <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mb-2">{v.label}</p>
                      <p className="text-2xl font-black text-white">{v.value} <span className="text-xs text-slate-600">{v.unit}</span></p>
                      <p className={`text-[10px] font-bold mt-1 ${v.color}`}>{v.status}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Appointments */}
              <div className="col-span-3 bg-[#1e293b] rounded-3xl p-6 border border-slate-800">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-black text-white uppercase tracking-widest flex items-center gap-2"><Calendar size={14} className="text-indigo-400" />Appointments</h3>
                  <button onClick={() => setActiveTab('Health Routine')} className="text-[9px] text-indigo-400 font-bold hover:text-indigo-300">Book New</button>
                </div>
                <div className="space-y-3">
                  {[
                    { date: 'MAY 21', day: 'TUE', name: 'Dr. Sharma', spec: 'Cardiologist', time: '10:30 AM', mode: 'Video' },
                    { date: 'MAY 28', day: 'TUE', name: 'Dr. Rao', spec: 'Neurologist', time: '11:00 AM', mode: 'In-clinic' },
                  ].map((apt, i) => (
                    <div key={i} className="flex gap-3 bg-[#0f172a] rounded-2xl p-3 border border-slate-800">
                      <div className="text-center min-w-[36px]">
                        <p className="text-[8px] font-black text-indigo-400 uppercase">{apt.date.split(' ')[0]}</p>
                        <p className="text-xl font-black text-white leading-tight">{apt.date.split(' ')[1]}</p>
                        <p className="text-[8px] text-slate-500 font-bold">{apt.day}</p>
                      </div>
                      <div className="flex-1">
                        <p className="text-[10px] font-black text-white">{apt.name}</p>
                        <p className="text-[9px] text-slate-400 font-bold">{apt.spec}</p>
                        <p className="text-[9px] text-slate-500 mt-1 flex items-center gap-1"><Clock size={8} />{apt.time} · {apt.mode}</p>
                        <span className="text-[8px] font-black bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full">Confirmed</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Row 2 */}
            <div className="grid grid-cols-12 gap-6">
              {/* Vitals Trends Chart */}
              <div className="col-span-5 bg-[#1e293b] rounded-3xl p-6 border border-slate-800">
                <h3 className="text-sm font-black text-white uppercase tracking-widest mb-4 flex items-center gap-2"><TrendingUp size={14} className="text-indigo-400" />Vitals & Trends</h3>
                <div className="h-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={weeklyData}>
                      <defs>
                        <linearGradient id="dashHR" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#f43f5e" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="dashSleep" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#0f172a" />
                      <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: '#475569', fontSize: 9, fontWeight: 'bold' }} />
                      <Tooltip contentStyle={{ backgroundColor: '#0f172a', border: 'none', borderRadius: '12px', fontSize: '11px' }} />
                      <Area type="monotone" dataKey="hr" stroke="#f43f5e" strokeWidth={2} fill="url(#dashHR)" />
                      <Area type="monotone" dataKey="sleep" stroke="#6366f1" strokeWidth={2} fill="url(#dashSleep)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex gap-4 mt-3">
                  <span className="flex items-center gap-1 text-[9px] font-bold text-rose-400"><span className="w-2 h-2 rounded-full bg-rose-500 inline-block" />Heart Rate</span>
                  <span className="flex items-center gap-1 text-[9px] font-bold text-indigo-400"><span className="w-2 h-2 rounded-full bg-indigo-500 inline-block" />Sleep Hours</span>
                </div>
              </div>

              {/* Health Goals */}
              <div className="col-span-4 bg-[#1e293b] rounded-3xl p-6 border border-slate-800">
                <h3 className="text-sm font-black text-white uppercase tracking-widest mb-4">Health Goals & Progress</h3>
                <div className="space-y-4">
                  {[
                    { label: 'Walk 8,000 steps/day', current: vitals.steps, target: 8000, unit: `${(vitals.steps || 0).toLocaleString()} steps`, color: 'bg-indigo-500' },
                    { label: 'Improve sleep score', current: Math.round((vitals.sleep / 9) * 100), target: 100, unit: `Score: ${Math.round((vitals.sleep / 9) * 100)}`, color: 'bg-purple-500' },
                    { label: 'Drink 2.5L water daily', current: vitals.hydration, target: 2.5, unit: `${vitals.hydration}L of 2.5L`, color: 'bg-sky-500' },
                    { label: 'Maintain healthy HR', current: vitals.hr <= 100 ? 80 : 40, target: 100, unit: `${vitals.hr} bpm`, color: 'bg-rose-500' },
                  ].map((g, i) => {
                    const pct = Math.min(100, Math.round(typeof g.current === 'number' ? (g.current / g.target) * 100 : 60));
                    return (
                      <div key={i}>
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-[10px] font-bold text-slate-300">{g.label}</p>
                          <span className="text-[10px] font-black text-white">{pct}%</span>
                        </div>
                        <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden mb-1">
                          <div className={`h-full rounded-full ${g.color} transition-all duration-700`} style={{ width: `${pct}%` }} />
                        </div>
                        <p className="text-[9px] text-slate-500">{g.unit}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Medications */}
              <div className="col-span-3 bg-[#1e293b] rounded-3xl p-6 border border-slate-800">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-black text-white uppercase tracking-widest flex items-center gap-2"><Bell size={14} className="text-amber-400" />Medications</h3>
                </div>
                <div className="space-y-3">
                  {medReminders.length === 0 ? (
                    <p className="text-slate-500 text-xs font-bold text-center py-4">No medications today</p>
                  ) : medReminders.map((med, i) => (
                    <div key={i} className={`flex items-center gap-3 p-3 rounded-2xl border ${med.taken ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-slate-800/50 border-slate-700'}`}>
                      <div className="flex-1">
                        <p className="text-[9px] text-slate-500 font-bold">{med.time}</p>
                        <p className="text-[10px] font-bold text-white truncate">💊 {med.name}</p>
                      </div>
                      {med.taken ? <CheckCircle2 size={14} className="text-emerald-400 shrink-0" /> :
                        <button onClick={() => {
                          setMedReminders(prev => {
                            const next = [...prev];
                            next[i].taken = true;
                            if (wsRef.current?.readyState === WebSocket.OPEN) {
                              wsRef.current.send(JSON.stringify({ source: 'web', user_id: patientId, reminders: next }));
                            }
                            return next;
                          });
                        }} className="text-[8px] font-black text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-full hover:bg-amber-500/20 shrink-0">Take</button>
                      }
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Row 3: AI Assistant + Health Score */}
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-4 bg-gradient-to-br from-indigo-600 to-purple-700 rounded-3xl p-6 relative overflow-hidden">
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-white/20 rounded-2xl flex items-center justify-center"><Sparkles size={20} className="text-white" /></div>
                    <div>
                      <p className="text-sm font-black text-white">AI Assistant</p>
                      <button onClick={() => setActiveTab('Wellness AI')} className="text-[9px] text-indigo-200 font-bold">Open Chat →</button>
                    </div>
                  </div>
                  <p className="text-xs text-indigo-100 font-medium leading-relaxed mb-4 italic">"{aiAnalysis}"</p>
                  <div className="flex flex-wrap gap-2">
                    {['Check my symptoms', 'Explain my labs', 'Medication reminder'].map(q => (
                      <button key={q} onClick={() => setActiveTab('Wellness AI')}
                        className="text-[9px] font-bold bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded-xl border border-white/20 transition-colors">
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="col-span-5 bg-[#1e293b] rounded-3xl p-6 border border-slate-800">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-black text-white uppercase tracking-widest flex items-center gap-2"><Activity size={14} className="text-indigo-400" />Recent Lab Results</h3>
                </div>
                <div className="space-y-3">
                  {[
                    { name: 'Lipid Panel', date: 'May 18, 2026', flag: 'Attention', color: 'amber', note: 'LDL cholesterol slightly high.' },
                    { name: 'HbA1c', date: 'May 18, 2026', flag: 'Normal', color: 'emerald', note: 'Average glucose is well controlled.' },
                    { name: 'Comprehensive Metabolic', date: 'May 16, 2026', flag: 'Normal', color: 'emerald', note: 'All values within normal range.' },
                  ].map((lab, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-[#0f172a] rounded-2xl border border-slate-800">
                      <div className="w-8 h-8 rounded-xl bg-slate-800 flex items-center justify-center shrink-0 text-sm">🧪</div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="text-[10px] font-black text-white">{lab.name}</p>
                          <span className={`text-[8px] font-black px-2 py-0.5 rounded-full ${lab.color === 'amber' ? 'bg-amber-500/20 text-amber-400' : 'bg-emerald-500/20 text-emerald-400'}`}>{lab.flag}</span>
                        </div>
                        <p className="text-[9px] text-slate-500">{lab.date}</p>
                        <p className="text-[9px] text-slate-400 mt-0.5">{lab.note}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="col-span-3 bg-[#1e293b] rounded-3xl p-6 border border-slate-800">
                <h3 className="text-sm font-black text-white uppercase tracking-widest mb-4">Health Score</h3>
                <div className="flex flex-col items-center mb-5">
                  <div className="relative w-28 h-28 mb-3">
                    <svg className="w-28 h-28 -rotate-90" viewBox="0 0 120 120">
                      <circle cx="60" cy="60" r="50" fill="none" stroke="#1e293b" strokeWidth="12" />
                      <circle cx="60" cy="60" r="50" fill="none" stroke="#6366f1" strokeWidth="12"
                        strokeDasharray={`${(healthScore || 87) * 3.14} 314`} strokeLinecap="round" />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <p className="text-3xl font-black text-white">{healthScore || 87}</p>
                      <p className="text-[9px] text-slate-500 font-bold">/100</p>
                    </div>
                  </div>
                  <p className="text-sm font-black text-emerald-400">Excellent</p>
                </div>
                <button onClick={async () => {
                  try {
                    const res = await apiClient.post(`/dashboard/generate-report?user_id=${patientId}`, null, { params: { current_hr: vitals.hr, current_sleep: vitals.sleep, current_steps: vitals.steps } });
                    setDashboardReport(res.data);
                  } catch (e) {}
                }} className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all active:scale-95">
                  View Full Health Report
                </button>
              </div>
            </div>

            {/* Emergency Alert */}
            {emergencyAlert && (
              <div className={`p-5 rounded-3xl border flex items-center gap-5 ${emergencyAlert.status === 'Emergency Alert' ? 'bg-rose-600/20 border-rose-600/50' : 'bg-amber-500/20 border-amber-500/50'}`}>
                <AlertTriangle size={32} className={`shrink-0 ${emergencyAlert.status === 'Emergency Alert' ? 'text-rose-500 animate-pulse' : 'text-amber-500'}`} />
                <div className="flex-1">
                  <p className={`text-xs font-black uppercase tracking-widest mb-1 ${emergencyAlert.status === 'Emergency Alert' ? 'text-rose-400' : 'text-amber-400'}`}>{emergencyAlert.status}</p>
                  <p className="text-white font-bold text-sm">{emergencyAlert.alert_message}</p>
                  <p className="text-slate-400 text-xs mt-1">{emergencyAlert.recommendation}</p>
                </div>
                {emergencyAlert.call_ambulance && <a href="tel:112" className="px-5 py-2.5 bg-rose-600 hover:bg-rose-700 text-white rounded-2xl font-black text-xs uppercase tracking-widest animate-pulse">Call 112</a>}
                <button onClick={() => setEmergencyAlert(null)} className="text-slate-500 hover:text-white p-2 rounded-xl hover:bg-slate-800 transition-colors">✕</button>
              </div>
            )}
          </div>
        )}

        {/* ════════════════════════════════════════════════════════
            ONBOARDING MODAL
        ════════════════════════════════════════════════════════ */}
        {isFirstTimeUser && (
          <div className="fixed inset-0 bg-slate-900/95 z-50 flex items-center justify-center p-4">
            {showIdModal ? (
              <div className="bg-[#1e293b] rounded-3xl p-8 max-w-md w-full shadow-2xl border border-slate-700 text-center">
                <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center border border-emerald-500/30 mx-auto mb-4">
                  <CheckCircle2 size={32} className="text-emerald-400" />
                </div>
                <h2 className="text-2xl font-black text-white uppercase mb-2">Registration Success!</h2>
                <p className="text-sm text-slate-400 mb-6">Your Medical ID — use this on your mobile app to link devices:</p>
                <div className="bg-[#0f172a] border border-slate-800 rounded-2xl p-4 mb-6 flex items-center justify-between gap-3">
                  <span className="text-xl font-mono font-black text-indigo-400 tracking-wider select-all">{generatedMedicalId}</span>
                  <button onClick={() => { navigator.clipboard.writeText(generatedMedicalId); alert('Copied!'); }}
                    className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-bold">Copy</button>
                </div>
                <button onClick={() => { setShowIdModal(false); setIsFirstTimeUser(false); setOnboardChoice(null); setActiveTab('Dashboard'); }}
                  className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-black uppercase tracking-widest text-xs transition-all active:scale-95">
                  🚀 Go to Dashboard
                </button>
              </div>
            ) : onboardChoice === null ? (
              <div className="bg-[#1e293b] rounded-3xl p-8 max-w-lg w-full shadow-2xl border border-slate-700 text-center">
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 bg-indigo-600/20 rounded-2xl flex items-center justify-center border border-indigo-500/30">
                    <Heart size={32} className="text-indigo-400" />
                  </div>
                </div>
                <h2 className="text-3xl font-black text-white uppercase mb-2">Welcome to VitalAI</h2>
                <p className="text-sm text-slate-400 mb-8">Personalized AI Health Monitoring</p>
                <div className="space-y-4">
                  <button onClick={() => setOnboardChoice('register')}
                    className="w-full py-5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-black uppercase tracking-widest text-xs transition-all active:scale-95">
                    👤 Create New Health Profile
                  </button>
                  <div className="text-slate-500 font-bold text-xs uppercase tracking-widest">or</div>
                  <button onClick={() => setOnboardChoice('connect')}
                    className="w-full py-5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-2xl font-black uppercase tracking-widest text-xs transition-all active:scale-95 border border-slate-700">
                    🔗 Link Existing Medical ID
                  </button>
                </div>
              </div>
            ) : onboardChoice === 'connect' ? (
              <div className="bg-[#1e293b] rounded-3xl p-8 max-w-md w-full shadow-2xl border border-slate-700">
                <button onClick={() => { setOnboardChoice(null); setConnectError(''); }} className="text-slate-400 hover:text-white text-xs font-bold flex items-center gap-1 mb-4">← Back</button>
                <h2 className="text-2xl font-black text-white uppercase mb-2">Link Existing Profile</h2>
                <p className="text-xs text-slate-400 mb-6">Enter the Medical ID from your registration.</p>
                <div className="space-y-4">
                  <input type="text" placeholder="e.g. VITAL-THAARANI-1234" value={enteredMedicalId} onChange={e => setEnteredMedicalId(e.target.value)}
                    className="w-full p-4 border border-slate-600 rounded-2xl text-white bg-[#0f172a] font-mono font-bold outline-none focus:ring-2 focus:ring-indigo-500" />
                  {connectError && <p className="text-xs text-rose-400 font-bold">{connectError}</p>}
                  <button onClick={handleConnectProfile}
                    className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-black uppercase tracking-widest text-xs transition-all active:scale-95">
                    Sync & Connect
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-[#1e293b] rounded-3xl p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-slate-700">
                <button onClick={() => setOnboardChoice(null)} className="text-slate-400 hover:text-white text-xs font-bold flex items-center gap-1 mb-4">← Back</button>
                <h2 className="text-3xl font-black text-white text-center uppercase mb-1">AI Health Profile Setup</h2>
                <p className="text-sm text-slate-400 text-center mb-8">Your data powers personalized health reports and AI reminders.</p>

                <p className="text-xs font-black text-indigo-400 uppercase tracking-widest mb-3">👤 Personal Information</p>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="col-span-2">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1 block">Full Name</label>
                    <input type="text" placeholder="e.g. Thaarani" value={profile.name}
                      className="w-full p-4 border border-slate-600 rounded-2xl text-white bg-[#0f172a] font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                      onChange={e => setProfile(p => ({ ...p, name: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1 block">Age</label>
                    <input type="number" placeholder="25" value={profile.age}
                      className="w-full p-4 border border-slate-600 rounded-2xl text-white bg-[#0f172a] font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                      onChange={e => setProfile(p => ({ ...p, age: parseInt(e.target.value) || 25 }))} />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1 block">Gender</label>
                    <select value={profile.gender} className="w-full p-4 border border-slate-600 rounded-2xl text-white bg-[#0f172a] font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                      onChange={e => setProfile(p => ({ ...p, gender: e.target.value }))}>
                      {['Female', 'Male', 'Other', 'Prefer not to say'].map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1 block">Blood Group</label>
                    <select value={profile.blood_group} className="w-full p-4 border border-slate-600 rounded-2xl text-white bg-[#0f172a] font-bold outline-none"
                      onChange={e => setProfile(p => ({ ...p, blood_group: e.target.value }))}>
                      {['O+', 'O-', 'A+', 'A-', 'B+', 'B-', 'AB+', 'AB-'].map(bg => <option key={bg} value={bg}>{bg}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1 block">Email</label>
                    <input type="email" placeholder="you@email.com" value={profile.email}
                      className="w-full p-4 border border-slate-600 rounded-2xl text-white bg-[#0f172a] font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                      onChange={e => setProfile(p => ({ ...p, email: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1 block">Phone</label>
                    <input type="tel" placeholder="+91 99999 00000" value={profile.phone_number}
                      className="w-full p-4 border border-slate-600 rounded-2xl text-white bg-[#0f172a] font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                      onChange={e => setProfile(p => ({ ...p, phone_number: e.target.value }))} />
                  </div>
                </div>

                <p className="text-xs font-black text-emerald-400 uppercase tracking-widest mb-3">❤️ Health & Lifestyle</p>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="col-span-2">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1 block">Chronic Conditions</label>
                    <input type="text" placeholder="e.g. BP, Diabetes, Asthma, None" value={profile.conditions}
                      className="w-full p-4 border border-slate-600 rounded-2xl text-white bg-[#0f172a] font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                      onChange={e => setProfile(p => ({ ...p, conditions: e.target.value }))} />
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1 block">Allergies</label>
                    <input type="text" placeholder="e.g. Peanuts, Shellfish, None" value={profile.allergies || ''}
                      className="w-full p-4 border border-slate-600 rounded-2xl text-white bg-[#0f172a] font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                      onChange={e => setProfile(p => ({ ...p, allergies: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1 block">Activity Level</label>
                    <select value={profile.activity} className="w-full p-4 border border-slate-600 rounded-2xl text-white bg-[#0f172a] font-bold outline-none"
                      onChange={e => setProfile(p => ({ ...p, activity: e.target.value }))}>
                      {['Sedentary', 'Lightly Active', 'Moderately Active', 'Very Active'].map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1 block">Diet Preference</label>
                    <select value={profile.restrictions} className="w-full p-4 border border-slate-600 rounded-2xl text-white bg-[#0f172a] font-bold outline-none"
                      onChange={e => setProfile(p => ({ ...p, restrictions: e.target.value }))}>
                      {['Vegetarian', 'Non-Vegetarian', 'Vegan', 'Keto', 'Paleo'].map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1 block">Cuisine Preference</label>
                    <select value={profile.cuisine} className="w-full p-4 border border-slate-600 rounded-2xl text-white bg-[#0f172a] font-bold outline-none"
                      onChange={e => setProfile(p => ({ ...p, cuisine: e.target.value }))}>
                      {['South Indian', 'North Indian', 'Chinese', 'Japanese', 'Korean', 'Thai', 'Vietnamese', 'Indonesian', 'Mexican', 'Italian', 'French', 'Spanish', 'Greek', 'Mediterranean', 'Lebanese', 'Turkish', 'Moroccan', 'Ethiopian', 'Brazilian', 'Peruvian'].map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1 block">Family Medical History</label>
                    <input type="text" placeholder="e.g. Diabetics" value={profile.family_history}
                      className="w-full p-4 border border-slate-600 rounded-2xl text-white bg-[#0f172a] font-bold outline-none"
                      onChange={e => setProfile(p => ({ ...p, family_history: e.target.value }))} />
                  </div>
                </div>

                <p className="text-xs font-black text-rose-400 uppercase tracking-widest mb-3">📍 Location</p>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1 block">City</label>
                    <input type="text" placeholder="Chennai" value={profile.city}
                      className="w-full p-4 border border-slate-600 rounded-2xl text-white bg-[#0f172a] font-bold outline-none"
                      onChange={e => setProfile(p => ({ ...p, city: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1 block">State</label>
                    <input type="text" placeholder="Tamil Nadu" value={profile.state}
                      className="w-full p-4 border border-slate-600 rounded-2xl text-white bg-[#0f172a] font-bold outline-none"
                      onChange={e => setProfile(p => ({ ...p, state: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1 block">Country</label>
                    <input type="text" placeholder="India" value={profile.country}
                      className="w-full p-4 border border-slate-600 rounded-2xl text-white bg-[#0f172a] font-bold outline-none"
                      onChange={e => setProfile(p => ({ ...p, country: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1 block">Pincode</label>
                    <input type="text" placeholder="600001" value={profile.pincode}
                      className="w-full p-4 border border-slate-600 rounded-2xl text-white bg-[#0f172a] font-bold outline-none"
                      onChange={e => setProfile(p => ({ ...p, pincode: e.target.value }))} />
                  </div>
                </div>

                <p className="text-xs font-black text-amber-400 uppercase tracking-widest mb-3">🚨 Emergency Contact</p>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1 block">Contact Name</label>
                    <input type="text" placeholder="e.g. Amma" value={profile.emergency_contact_name}
                      className="w-full p-4 border border-slate-600 rounded-2xl text-white bg-[#0f172a] font-bold outline-none"
                      onChange={e => setProfile(p => ({ ...p, emergency_contact_name: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1 block">Contact Phone</label>
                    <input type="tel" placeholder="+91 98765 43210" value={profile.emergency_contact_phone}
                      className="w-full p-4 border border-slate-600 rounded-2xl text-white bg-[#0f172a] font-bold outline-none"
                      onChange={e => setProfile(p => ({ ...p, emergency_contact_phone: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1 block">Relationship</label>
                    <input type="text" placeholder="Mother" value={profile.relationship}
                      className="w-full p-4 border border-slate-600 rounded-2xl text-white bg-[#0f172a] font-bold outline-none"
                      onChange={e => setProfile(p => ({ ...p, relationship: e.target.value }))} />
                  </div>
                </div>

                <button id="submitProfile" onClick={handleOnboarding}
                  className="w-full py-5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-[1.5rem] font-black uppercase tracking-widest text-sm shadow-xl active:scale-95 transition-all">
                  🚀 Save Profile & Generate AI Report
                </button>
              </div>
            )}
          </div>
        )}

        {/* Food Scanner Canvas */}
        <canvas ref={canvasRef} className="hidden" />

        {/* Food Scanner Overlay */}
        {isScanningFood && (
          <div className="fixed inset-0 bg-black/95 z-[999] flex flex-col items-center justify-center p-6 backdrop-blur-xl">
            <div className="w-full max-w-3xl relative">
              <div className="absolute -top-16 left-0 right-0 flex justify-between items-center text-white z-10">
                <h3 className="text-2xl font-black uppercase tracking-widest flex items-center gap-3">
                  <span className="text-3xl">📸</span> AI Food Scanner
                </h3>
                <button onClick={() => {
                  console.log('Closing camera...');
                  const stream = videoRef.current?.srcObject;
                  if (stream) stream.getTracks().forEach(t => t.stop());
                  setIsScanningFood(false);
                  setScannedFood(null);
                }} className="px-4 py-2 bg-rose-500/20 hover:bg-rose-500/30 border border-rose-500/30 rounded-xl text-white font-bold transition-all">
                  ✕ CLOSE
                </button>
              </div>
              
              <div className="relative aspect-video bg-slate-900 rounded-3xl overflow-hidden border-4 border-indigo-500/40 shadow-2xl">
                <video 
                  ref={videoRef} 
                  className="w-full h-full object-cover" 
                  playsInline 
                  muted 
                  autoPlay
                  onLoadedMetadata={(e) => {
                    console.log('✅ Video loaded:', e.target.videoWidth, 'x', e.target.videoHeight);
                  }}
                />
                
                {/* Scanner Overlay Guide */}
                {!scannedFood && (
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute inset-0 border-4 border-dashed border-emerald-400/50 animate-pulse m-8 rounded-2xl" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center bg-black/70 px-6 py-3 rounded-2xl">
                      <p className="text-white font-black text-lg">Center your food in the frame</p>
                      <p className="text-slate-300 text-sm mt-1">Then click "Capture & Analyze"</p>
                    </div>
                  </div>
                )}
                
                {scannedFood && (
                  <div className="absolute inset-0 bg-slate-900/98 flex flex-col items-center justify-center p-10 text-center backdrop-blur-xl">
                    <div className="mb-4 w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center animate-bounce">
                      <span className="text-3xl">✓</span>
                    </div>
                    <h2 className="text-5xl font-black text-white uppercase mb-6 bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
                      {scannedFood.detected || 'Analysis Complete'}
                    </h2>
                    <div className="grid grid-cols-4 gap-6 w-full max-w-2xl">
                      {[
                        { label: 'Calories', value: `${scannedFood?.nutrition?.calories || 0}`, color: 'from-amber-500 to-orange-500', icon: '🔥' },
                        { label: 'Protein', value: `${scannedFood?.nutrition?.protein || 0}g`, color: 'from-emerald-500 to-teal-500', icon: '💪' },
                        { label: 'Carbs', value: `${scannedFood?.nutrition?.carbs || 0}g`, color: 'from-sky-500 to-blue-500', icon: '🌾' },
                        { label: 'Fat', value: `${scannedFood?.nutrition?.fat || 0}g`, color: 'from-rose-500 to-pink-500', icon: '🧈' },
                      ].map(m => (
                        <div key={m.label} className={`bg-gradient-to-br ${m.color} bg-opacity-10 p-6 rounded-3xl border-2 border-white/10`}>
                          <p className="text-2xl mb-2">{m.icon}</p>
                          <p className="text-[10px] font-bold text-white/70 uppercase tracking-widest mb-2">{m.label}</p>
                          <p className="text-3xl font-black text-white">{m.value}</p>
                        </div>
                      ))}
                    </div>
                    <button onClick={() => {
                      const stream = videoRef.current?.srcObject;
                      if (stream) stream.getTracks().forEach(t => t.stop());
                      setIsScanningFood(false);
                      setActiveTab('Health Vitals'); // Navigate to see full details
                    }} className="mt-8 px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white rounded-2xl font-black uppercase tracking-widest text-sm shadow-2xl active:scale-95 transition-all">
                      View Full Details →
                    </button>
                  </div>
                )}
              </div>
              
              {!scannedFood && (
                <button onClick={captureFood} disabled={!videoRef.current?.videoWidth} 
                  className="mt-8 w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 disabled:from-slate-700 disabled:to-slate-600 disabled:cursor-not-allowed text-white py-6 rounded-2xl font-black uppercase tracking-widest text-xl shadow-2xl active:scale-95 transition-all flex items-center justify-center gap-3">
                  <span className="text-2xl">📸</span> Capture & Analyze
                </button>
              )}
            </div>
          </div>
        )}

        {/* Hydration Popup */}
        {showHydrationPopup && (
          <div className="fixed bottom-10 right-10 z-[110]">
            <div className="bg-sky-600 text-white p-6 rounded-3xl shadow-2xl border border-sky-400/30 flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center"><Droplets size={24} className="animate-pulse" /></div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest opacity-80">Hydration Reminder</p>
                <p className="text-lg font-black">Time to drink water!</p>
              </div>
              <button onClick={() => setShowHydrationPopup(false)} className="ml-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20">✕</button>
            </div>
          </div>
        )}

        {/* ── FIX: Medication Reminder Popup is NOW INSIDE return() ── */}
        {medReminderPopup && (
          <div className="fixed top-6 right-6 z-[200] max-w-sm">
            <div className="bg-indigo-600 text-white p-5 rounded-3xl shadow-[0_20px_60px_rgba(79,70,229,0.5)] border border-indigo-400/30">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center shrink-0 animate-bounce">
                  <span className="text-2xl">💊</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-black uppercase tracking-widest opacity-80 mb-1">Medication Reminder</p>
                  <p className="text-base font-black leading-tight">{medReminderPopup.name}</p>
                  <p className="text-xs font-bold opacity-70 mt-1">Scheduled: {medReminderPopup.time}</p>
                </div>
                <button onClick={() => setMedReminderPopup(null)}
                  className="w-7 h-7 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 font-bold text-sm">✕</button>
              </div>
              <div className="flex gap-3 mt-4">
                <button onClick={() => {
                  const updated = [...medReminders];
                  updated[medReminderPopup.index].taken = true;
                  setMedReminders(updated);
                  setMedReminderPopup(null);
                  if (wsRef.current?.readyState === WebSocket.OPEN) {
                    wsRef.current.send(JSON.stringify({ source: 'web', user_id: patientId, reminders: updated }));
                  }
                  apiClient.post('/health-connect/medications', {
                    user_id: patientId, date: new Date().toDateString(),
                    medications: updated, taken_count: updated.filter(m => m.taken).length, total_count: updated.length
                  }).catch(() => {});
                  speakResponse("Great! Medication marked as taken.", selectedVoice);
                }} className="flex-1 py-2.5 bg-white text-indigo-600 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-indigo-50 transition-all active:scale-95">
                  ✅ Mark Taken
                </button>
                <button onClick={() => {
                  const popup = medReminderPopup;
                  setMedReminderPopup(null);
                  setTimeout(() => {
                    setMedReminderPopup(popup);
                    speakResponse(`Snooze reminder: Please take ${popup.name} now.`, selectedVoice);
                  }, 15 * 60 * 1000);
                  speakResponse("Snoozed for 15 minutes.", selectedVoice);
                }} className="flex-1 py-2.5 bg-white/20 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-white/30 transition-all border border-white/20">
                  ⏰ Snooze 15m
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Global Voice Button */}
        <div className="fixed bottom-10 right-1/2 translate-x-1/2 z-[150]">
          <button onClick={handleGlobalVoiceInput}
            className={`w-16 h-16 flex items-center justify-center rounded-full shadow-2xl transition-all ${isListeningGlobal ? 'bg-rose-500 text-white animate-pulse scale-110' : isProcessingVoice ? 'bg-amber-500 text-white' : 'bg-indigo-600 hover:bg-indigo-500 text-white hover:scale-105'}`}>
            {isListeningGlobal ? <MicOff size={28} /> : isProcessingVoice ? <Loader2 className="animate-spin" size={28} /> : <Mic size={28} />}
          </button>
        </div>

        {/* Debug Log */}
        <div className="fixed bottom-4 left-4 z-[200] bg-black/80 p-3 rounded-xl border border-white/10 text-[10px] font-mono text-emerald-400 pointer-events-none">
          {debugLogs.map((log, i) => (
            <div key={i} className={i === 0 ? 'text-white font-bold' : 'opacity-50'}>{`> ${log}`}</div>
          ))}
        </div>

      </main>
    </div>
  );
}

export default App;